/**
 * Modelos, DTOs, Requests y Enums generados desde la API (api-docs.json).
 * Tipos reutilizables en todo el proyecto.
 */

// =============================================================================
// ENUMS
// =============================================================================

/** Tipo de usuario en la cuenta */
export const UserType = {
  OWNER: 'OWNER',
  USER: 'USER'
} as const;
export type UserTypeValue = (typeof UserType)[keyof typeof UserType];

/** Estado activo/inactivo (usuarios, productos, clientes, etc.) */
export const EntityState = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE'
} as const;
export type EntityStateValue = (typeof EntityState)[keyof typeof EntityState];

/** Roles de usuario */
export const RoleEnum = {
  DEFAULT_USER_PERMISSIONS: 'DEFAULT_USER_PERMISSIONS',
  SALES: 'SALES',
  BILLING: 'BILLING',
  COMPANY_ADMIN: 'COMPANY_ADMIN',
  ADMIN: 'ADMIN'
} as const;
export type RoleEnumValue = (typeof RoleEnum)[keyof typeof RoleEnum];

/** Permisos del sistema */
export const PermissionEnum = {
  SCHEDULERS: 'SCHEDULERS',
  WEBHOOK_WRITE: 'WEBHOOK_WRITE',
  WEBHOOK_READ: 'WEBHOOK_READ',
  PAYMENT_FORM_SEARCH: 'PAYMENT_FORM_SEARCH',
  PAYMENT_FORM_LIST: 'PAYMENT_FORM_LIST',
  PAYMENT_METHOD_SEARCH: 'PAYMENT_METHOD_SEARCH',
  PAYMENT_METHOD_LIST: 'PAYMENT_METHOD_LIST',
  SERIE_SEARCH: 'SERIE_SEARCH',
  SERIE_LIST: 'SERIE_LIST',
  SERIE_READ: 'SERIE_READ',
  SERIE_WRITE: 'SERIE_WRITE',
  SERIE_DELETE: 'SERIE_DELETE',
  TAX_SEARCH: 'TAX_SEARCH',
  TAX_LIST: 'TAX_LIST',
  PAYMENT_LINK_SEARCH: 'PAYMENT_LINK_SEARCH',
  PAYMENT_LINK_LIST: 'PAYMENT_LINK_LIST',
  TAX_PRODUCT_CODE_SEARCH: 'TAX_PRODUCT_CODE_SEARCH',
  TAX_PRODUCT_CODE_WRITE: 'TAX_PRODUCT_CODE_WRITE',
  TAX_PRODUCT_CODE_READ: 'TAX_PRODUCT_CODE_READ',
  TAX_PRODUCT_CODE_LIST: 'TAX_PRODUCT_CODE_LIST',
  MEASURE_UNIT_SEARCH: 'MEASURE_UNIT_SEARCH',
  MEASURE_UNIT_WRITE: 'MEASURE_UNIT_WRITE',
  MEASURE_UNIT_READ: 'MEASURE_UNIT_READ',
  MEASURE_UNIT_LIST: 'MEASURE_UNIT_LIST',
  CURRENCY_SEARCH: 'CURRENCY_SEARCH',
  CURRENCY_WRITE: 'CURRENCY_WRITE',
  CURRENCY_READ: 'CURRENCY_READ',
  CURRENCY_LIST: 'CURRENCY_LIST',
  CUSTOMER_SEARCH: 'CUSTOMER_SEARCH',
  CUSTOMER_WRITE: 'CUSTOMER_WRITE',
  CUSTOMER_READ: 'CUSTOMER_READ',
  CUSTOMER_LIST: 'CUSTOMER_LIST',
  CUSTOMER_DELETE: 'CUSTOMER_DELETE',
  COMPANY_DELETE: 'COMPANY_DELETE',
  COMPANY_READ: 'COMPANY_READ',
  COMPANY_SEARCH: 'COMPANY_SEARCH',
  COMPANY_WRITE: 'COMPANY_WRITE',
  COMPANY_CONFIG_WRITE: 'COMPANY_CONFIG_WRITE',
  COUNTRY_SEARCH: 'COUNTRY_SEARCH',
  USER_DELETE: 'USER_DELETE',
  USER_READ: 'USER_READ',
  USER_SEARCH: 'USER_SEARCH',
  USER_WRITE: 'USER_WRITE',
  PRODUCT_DELETE: 'PRODUCT_DELETE',
  PRODUCT_READ: 'PRODUCT_READ',
  PRODUCT_SEARCH: 'PRODUCT_SEARCH',
  PRODUCT_WRITE: 'PRODUCT_WRITE',
  PRODUCT_LIST: 'PRODUCT_LIST',
  INVOICE_DELETE: 'INVOICE_DELETE',
  INVOICE_SIGN: 'INVOICE_SIGN',
  INVOICE_CANCEL: 'INVOICE_CANCEL',
  INVOICE_READ: 'INVOICE_READ',
  INVOICE_SEARCH: 'INVOICE_SEARCH',
  INVOICE_WRITE: 'INVOICE_WRITE',
  INVOICE_LIST: 'INVOICE_LIST',
  SALE_DELETE: 'SALE_DELETE',
  SALE_SIGN: 'SALE_SIGN',
  SALE_CANCEL: 'SALE_CANCEL',
  SALE_READ: 'SALE_READ',
  SALE_SEARCH: 'SALE_SEARCH',
  SALE_WRITE: 'SALE_WRITE',
  SALE_LIST: 'SALE_LIST',
  SALE_EXPORT: 'SALE_EXPORT',
  MIT_MANDATE_EXPORT: 'MIT_MANDATE_EXPORT',
  MIT_TRANSACTION_EXPORT: 'MIT_TRANSACTION_EXPORT',
  PLAN_DELETE: 'PLAN_DELETE',
  PLAN_READ: 'PLAN_READ',
  PLAN_SEARCH: 'PLAN_SEARCH',
  PLAN_WRITE: 'PLAN_WRITE',
  PLAN_LIST: 'PLAN_LIST',
  SUBSCRIPTION_DELETE: 'SUBSCRIPTION_DELETE',
  SUBSCRIPTION_READ: 'SUBSCRIPTION_READ',
  SUBSCRIPTION_SEARCH: 'SUBSCRIPTION_SEARCH',
  SUBSCRIPTION_WRITE: 'SUBSCRIPTION_WRITE',
  SUBSCRIPTION_LIST: 'SUBSCRIPTION_LIST',
  SUBSCRIPTION_EXPORT: 'SUBSCRIPTION_EXPORT',
  CHARGE_DELETE: 'CHARGE_DELETE',
  CHARGE_READ: 'CHARGE_READ',
  CHARGE_SEARCH: 'CHARGE_SEARCH',
  CHARGE_WRITE: 'CHARGE_WRITE',
  CHARGE_LIST: 'CHARGE_LIST',
  CHARGE_EXPORT: 'CHARGE_EXPORT',
  PAYMENT_READ: 'PAYMENT_READ',
  PAYMENT_SEARCH: 'PAYMENT_SEARCH',
  PAYMENT_LIST: 'PAYMENT_LIST',
  PAYMENT_EXPORT: 'PAYMENT_EXPORT',
  FORECAST_CASH_FLOW_READ: 'FORECAST_CASH_FLOW_READ',
  FORECAST_AGING_READ: 'FORECAST_AGING_READ',
  FORECAST_CHURN_READ: 'FORECAST_CHURN_READ',
  FORECAST_PIPELINE_READ: 'FORECAST_PIPELINE_READ',
  FORECAST_COLLECTION_CALENDAR_READ: 'FORECAST_COLLECTION_CALENDAR_READ',
  RECONCILIATION_READ: 'RECONCILIATION_READ',
  BANK_ACCOUNT_LIST: 'BANK_ACCOUNT_LIST',
  BANK_ACCOUNT_SEARCH: 'BANK_ACCOUNT_SEARCH',
  BANK_ACCOUNT_READ: 'BANK_ACCOUNT_READ',
  BANK_ACCOUNT_WRITE: 'BANK_ACCOUNT_WRITE',
  BANK_ACCOUNT_DELETE: 'BANK_ACCOUNT_DELETE',
  BANK_RECONCILIATION_LIST: 'BANK_RECONCILIATION_LIST',
  BANK_RECONCILIATION_SEARCH: 'BANK_RECONCILIATION_SEARCH',
  BANK_RECONCILIATION_READ: 'BANK_RECONCILIATION_READ',
  BANK_RECONCILIATION_WRITE: 'BANK_RECONCILIATION_WRITE',
  BANK_RECONCILIATION_DELETE: 'BANK_RECONCILIATION_DELETE'
} as const;
export type PermissionEnumValue = (typeof PermissionEnum)[keyof typeof PermissionEnum];

/** Tipo de impuesto */
export const TaxTypeEnum = {
  VAT: 'VAT',
  SALES_TAX: 'SALES_TAX',
  EXCISE: 'EXCISE',
  WITHHOLDING: 'WITHHOLDING',
  INCOME_TAX: 'INCOME_TAX',
  OTHER: 'OTHER'
} as const;
export type TaxTypeEnumValue = (typeof TaxTypeEnum)[keyof typeof TaxTypeEnum];

/** Tipo de aplicación de impuesto */
export const TaxApplicationTypeEnum = {
  TAXABLE: 'TAXABLE',
  EXEMPT: 'EXEMPT',
  ZERO_RATED: 'ZERO_RATED',
  REVERSE_CHARGE: 'REVERSE_CHARGE'
} as const;
export type TaxApplicationTypeEnumValue = (typeof TaxApplicationTypeEnum)[keyof typeof TaxApplicationTypeEnum];

/** Tipo de cobro de impuesto (traslado o retención) */
export const TaxCollectionTypeEnum = {
  SELLER_COLLECTS: 'SELLER_COLLECTS',
  SELLER_WITHHOLDS: 'SELLER_WITHHOLDS'
} as const;
export type TaxCollectionTypeEnumValue = (typeof TaxCollectionTypeEnum)[keyof typeof TaxCollectionTypeEnum];

/** Tipo de dirección */
export const AddressTypeEnum = {
  BILLING: 'BILLING',
  PRIMARY: 'PRIMARY',
  SHIPPING: 'SHIPPING',
  CONTACT: 'CONTACT',
  OTHER: 'OTHER'
} as const;
export type AddressTypeEnumValue = (typeof AddressTypeEnum)[keyof typeof AddressTypeEnum];

/** Tipo de contacto */
export const ContactTypeEnum = {
  PRIMARY: 'PRIMARY',
  OFFICE: 'OFFICE',
  HOME: 'HOME',
  MOBILE: 'MOBILE',
  FAX: 'FAX',
  EMAIL: 'EMAIL',
  WEBSITE: 'WEBSITE',
  SOCIAL_MEDIA: 'SOCIAL_MEDIA',
  OTHER: 'OTHER'
} as const;
export type ContactTypeEnumValue = (typeof ContactTypeEnum)[keyof typeof ContactTypeEnum];

/** Tipo de persona (API: `CompanyType`) */
export const CompanyTypeEnum = {
  PERSON: 'PERSON',
  COMPANY: 'COMPANY'
} as const;
export type CompanyTypeEnumValue = (typeof CompanyTypeEnum)[keyof typeof CompanyTypeEnum];

export const CompanyTypeLabels: Record<CompanyTypeEnumValue, string> = {
  [CompanyTypeEnum.PERSON]: 'Física',
  [CompanyTypeEnum.COMPANY]: 'Moral'
};

export const CompanyTypeOptions: { value: CompanyTypeEnumValue; label: string }[] = [
  { value: CompanyTypeEnum.PERSON, label: CompanyTypeLabels[CompanyTypeEnum.PERSON] },
  { value: CompanyTypeEnum.COMPANY, label: CompanyTypeLabels[CompanyTypeEnum.COMPANY] }
];

export function getCompanyTypeLabel(type: CompanyTypeEnumValue | string | undefined | null): string {
  if (type === CompanyTypeEnum.PERSON || type === CompanyTypeEnum.COMPANY) {
    return CompanyTypeLabels[type];
  }
  return type ? String(type) : '';
}

/** Tipo de facturación */
export const BillingTypeEnum = {
  FISCAL: 'FISCAL',
  PROFORMA: 'PROFORMA'
} as const;
export type BillingTypeEnumValue = (typeof BillingTypeEnum)[keyof typeof BillingTypeEnum];

/** Método de cobro */
export const CollectionMethodEnum = {
  LINK: 'LINK',
  MANUAL: 'MANUAL',
  RECURRING_CHARGE: 'RECURRING_CHARGE',
  /** Cobro bajo demanda contra un mandato MIT; solo lo genera la API. */
  MERCHANT_INITIATED: 'MERCHANT_INITIATED'
} as const;
export type CollectionMethodEnumValue = (typeof CollectionMethodEnum)[keyof typeof CollectionMethodEnum];

/** Frecuencia de pago */
export const FrequencyEnum = {
  ONCE: 'ONCE',
  DAILY: 'DAILY',
  WEEKLY: 'WEEKLY',
  BIWEEKLY: 'BIWEEKLY',
  MONTHLY: 'MONTHLY',
  QUARTERLY: 'QUARTERLY',
  SEMIANNUALLY: 'SEMIANNUALLY',
  ANNUALLY: 'ANNUALLY'
} as const;
export type FrequencyEnumValue = (typeof FrequencyEnum)[keyof typeof FrequencyEnum];

/** Selección de día */
export const DaySelectionEnum = {
  SPECIFIC_DAY: 'SPECIFIC_DAY',
  OFFSET_DAYS: 'OFFSET_DAYS'
} as const;
export type DaySelectionEnumValue = (typeof DaySelectionEnum)[keyof typeof DaySelectionEnum];

/** Estado de factura / venta operativa (`Sale.status` / `InvoiceStatus`) */
export const InvoiceStatusEnum = {
  DRAFT: 'DRAFT',
  ISSUED: 'ISSUED',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  PAID: 'PAID',
  /** Legacy / facturas; preferir CANCELLED para ventas */
  VOID: 'VOID',
  /** Venta cancelada (`SaleStatus.CANCELLED`) */
  CANCELLED: 'CANCELLED',
  UNCOLLECTIBLE: 'UNCOLLECTIBLE'
} as const;
export type InvoiceStatusEnumValue = (typeof InvoiceStatusEnum)[keyof typeof InvoiceStatusEnum];

/** Estado SAT/PAC del CFDI (API: `Invoice.status` / `InvoiceStatusEnum` Java). */
export const CfdiInvoiceStatusEnum = {
  STAMPING: 'STAMPING',
  ACTIVE: 'ACTIVE',
  CANCELLATION_IN_PROGRESS: 'CANCELLATION_IN_PROGRESS',
  CANCELLED: 'CANCELLED'
} as const;
export type CfdiInvoiceStatusEnumValue =
  (typeof CfdiInvoiceStatusEnum)[keyof typeof CfdiInvoiceStatusEnum];

/**
 * Etapa comercial del embudo (API: `Sale.processStatus` / `SaleProcessStatusEnum`).
 * Orden alineado al catálogo `sale_process_status`.
 */
export const SalePipelineStatusEnum = {
  ACCEPTED: 'ACCEPTED',
  CONTRACT_ATTACHMENT: 'CONTRACT_ATTACHMENT',
  OC: 'OC',
  INVOICED: 'INVOICED',
  INVOICE_IMPORTED: 'INVOICE_IMPORTED',
  PAID: 'PAID',
  LOST: 'LOST'
} as const;
export type SalePipelineStatusEnumValue =
  (typeof SalePipelineStatusEnum)[keyof typeof SalePipelineStatusEnum];

/**
 * Alias con el nombre del enum Java (`SaleProcessStatusEnum`) para `processStatus`.
 * Preferir `SalePipelineStatusEnum` en código de UI.
 */
export const SaleProcessStatusCatalogEnum = SalePipelineStatusEnum;
export type SaleProcessStatusCatalogEnumValue = SalePipelineStatusEnumValue;

/**
 * @deprecated Preferir `InvoiceStatusEnum` para `Sale.status`.
 * Se mantiene por formularios legacy que aún referencian el ciclo operativo antiguo.
 */
export const SaleProcessStatusEnum = {
  DRAFT: 'DRAFT',
  QUOTATION: 'QUOTATION',
  OC: 'OC',
  IN_BILLING: 'IN_BILLING',
  BILLED: 'BILLED'
} as const;
export type SaleProcessStatusEnumValue =
  (typeof SaleProcessStatusEnum)[keyof typeof SaleProcessStatusEnum];

/** Venta única vs suscripción (API: `saleType` / `SaleType`) */
export const SaleTypeEnum = {
  ONE_TIME: 'ONE_TIME',
  SUBSCRIPTION: 'SUBSCRIPTION'
} as const;
export type SaleTypeEnumValue = (typeof SaleTypeEnum)[keyof typeof SaleTypeEnum];

/** Morosidad de la venta (API `DelinquencyStatus`) */
export const DelinquencyStatusEnum = {
  CURRENT: 'CURRENT',
  OVERDUE: 'OVERDUE'
} as const;
export type DelinquencyStatusEnumValue =
  (typeof DelinquencyStatusEnum)[keyof typeof DelinquencyStatusEnum];

/** Estado operativo del cargo (API `ChargeDTO.status`) */
export const ChargeOperationalStatusEnum = {
  SCHEDULED: 'SCHEDULED',
  PENDING: 'PENDING',
  DUE: 'DUE',
  PAID: 'PAID',
  FAILED: 'FAILED',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED'
} as const;
export type ChargeOperationalStatusEnumValue =
  (typeof ChargeOperationalStatusEnum)[keyof typeof ChargeOperationalStatusEnum];

/** `taxType` en líneas de impuesto de venta/factura (API `SaleLineTaxDTO` / `InvoiceLineTaxDTO`) */
export const LineTaxTypeEnum = {
  VAT: 'VAT',
  EXCISE: 'EXCISE',
  INCOME_TAX: 'INCOME_TAX'
} as const;
export type LineTaxTypeEnumValue = (typeof LineTaxTypeEnum)[keyof typeof LineTaxTypeEnum];

/** `applicationType` en líneas de impuesto (API; sin `REVERSE_CHARGE`) */
export const LineTaxApplicationTypeEnum = {
  TAXABLE: 'TAXABLE',
  EXEMPT: 'EXEMPT',
  ZERO_RATED: 'ZERO_RATED'
} as const;
export type LineTaxApplicationTypeEnumValue =
  (typeof LineTaxApplicationTypeEnum)[keyof typeof LineTaxApplicationTypeEnum];

/**
 * Convierte el tipo de aplicación de impuesto al subconjunto que acepta la API en
 * `CreateInvoiceLineTaxRequest` / `CreateSaleLineTaxRequest` (OpenAPI sin `REVERSE_CHARGE`).
 */
export function toLineTaxApplicationTypeForCreate(
  v: TaxApplicationTypeEnumValue | LineTaxApplicationTypeEnumValue | undefined
): LineTaxApplicationTypeEnumValue {
  if (v === 'REVERSE_CHARGE') return LineTaxApplicationTypeEnum.TAXABLE;
  if (v === 'EXEMPT' || v === 'ZERO_RATED' || v === 'TAXABLE') return v;
  return LineTaxApplicationTypeEnum.TAXABLE;
}

/** Estado del plan de suscripción */
export const PlanStatusEnum = {
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
  ARCHIVED: 'ARCHIVED'
} as const;
export type PlanStatusEnumValue = (typeof PlanStatusEnum)[keyof typeof PlanStatusEnum];

/** Proveedor de pago (API `PaymentDTO.paymentProvider`) */
export const PaymentProviderEnum = {
  PENTO: 'PENTO',
  PAYCODE: 'PAYCODE',
  NETPAY: 'NETPAY'
} as const;
export type PaymentProviderEnumValue = (typeof PaymentProviderEnum)[keyof typeof PaymentProviderEnum];

/** Estado del registro de pago (API `PaymentDTO.status`) */
export const PaymentRecordStatusEnum = {
  PAID: 'PAID',
  CANCELLED: 'CANCELLED',
  FAILED: 'FAILED',
  PENDING: 'PENDING',
  EXPIRED: 'EXPIRED'
} as const;
export type PaymentRecordStatusEnumValue =
  (typeof PaymentRecordStatusEnum)[keyof typeof PaymentRecordStatusEnum];

/** Método de pago en registro de pago (API `PaymentDTO.paymentMethod`) */
export const PaymentRecordMethodEnum = {
  CREDIT_CARD: 'CREDIT_CARD',
  DEBIT_CARD: 'DEBIT_CARD',
  CASH: 'CASH',
  TRANSFER: 'TRANSFER',
  CHECK: 'CHECK',
  SPEI: 'SPEI',
  COD: 'COD',
  OTHER: 'OTHER'
} as const;
export type PaymentRecordMethodEnumValue =
  (typeof PaymentRecordMethodEnum)[keyof typeof PaymentRecordMethodEnum];

export const PaymentRecordMethodLabels: Record<PaymentRecordMethodEnumValue, string> = {
  [PaymentRecordMethodEnum.CREDIT_CARD]: 'Tarjeta de crédito',
  [PaymentRecordMethodEnum.DEBIT_CARD]: 'Tarjeta de débito',
  [PaymentRecordMethodEnum.CASH]: 'Efectivo',
  [PaymentRecordMethodEnum.TRANSFER]: 'Transferencia',
  [PaymentRecordMethodEnum.CHECK]: 'Cheque',
  [PaymentRecordMethodEnum.SPEI]: 'SPEI',
  [PaymentRecordMethodEnum.COD]: 'Contra entrega',
  [PaymentRecordMethodEnum.OTHER]: 'Otro'
};

export const PaymentRecordMethodOptions: { value: PaymentRecordMethodEnumValue; label: string }[] = (
  Object.keys(PaymentRecordMethodLabels) as PaymentRecordMethodEnumValue[]
).map((value) => ({ value, label: PaymentRecordMethodLabels[value] }));

export function getPaymentRecordMethodLabel(method: PaymentRecordMethodEnumValue | string | undefined | null): string {
  if (method && method in PaymentRecordMethodLabels) {
    return PaymentRecordMethodLabels[method as PaymentRecordMethodEnumValue];
  }
  return method ? String(method) : '';
}

export function getPaymentFormLabel(
  form: { description?: string; name?: string; code?: string } | undefined | null
): string {
  if (!form) return '';
  const desc = (form.description ?? '').trim();
  if (desc) return desc;
  const name = (form.name ?? '').trim();
  if (name) return name;
  return (form.code ?? '').trim();
}

export const PaymentRecordStatusLabels: Record<PaymentRecordStatusEnumValue, string> = {
  [PaymentRecordStatusEnum.PAID]: 'Pagado',
  [PaymentRecordStatusEnum.CANCELLED]: 'Cancelado',
  [PaymentRecordStatusEnum.FAILED]: 'Fallido',
  [PaymentRecordStatusEnum.PENDING]: 'Pendiente',
  [PaymentRecordStatusEnum.EXPIRED]: 'Expirado'
};

export function getPaymentRecordStatusLabel(status: PaymentRecordStatusEnumValue | string | undefined | null): string {
  if (status && status in PaymentRecordStatusLabels) {
    return PaymentRecordStatusLabels[status as PaymentRecordStatusEnumValue];
  }
  return status ? String(status) : '';
}

export const PaymentProviderLabels: Record<PaymentProviderEnumValue, string> = {
  [PaymentProviderEnum.PENTO]: 'Pento',
  [PaymentProviderEnum.PAYCODE]: 'Paycode',
  [PaymentProviderEnum.NETPAY]: 'NetPay'
};

export function getPaymentProviderLabel(provider: PaymentProviderEnumValue | string | undefined | null): string {
  if (provider && provider in PaymentProviderLabels) {
    return PaymentProviderLabels[provider as PaymentProviderEnumValue];
  }
  return provider ? String(provider) : '';
}

/** Comportamiento de impuesto en línea */
export const TaxBehaviorEnum = {
  AUTOMATIC: 'AUTOMATIC',
  MANUAL: 'MANUAL',
  EXEMPT: 'EXEMPT',
  ZERO_RATED: 'ZERO_RATED',
  INCLUSIVE: 'INCLUSIVE',
  EXCLUSIVE: 'EXCLUSIVE'
} as const;
export type TaxBehaviorEnumValue = (typeof TaxBehaviorEnum)[keyof typeof TaxBehaviorEnum];

/** Proveedor de link de pago */
export const PaymentLinkProviderEnum = {
  PAYCODE: 'PAYCODE'
} as const;
export type PaymentLinkProviderEnumValue = (typeof PaymentLinkProviderEnum)[keyof typeof PaymentLinkProviderEnum];

/** Estado del link de pago */
export const PaymentLinkStatusEnum = {
  PAID: 'PAID',
  DUE: 'DUE',
  CANCELLED: 'CANCELLED',
  FAILED: 'FAILED',
  PENDING: 'PENDING',
  EXPIRED: 'EXPIRED'
} as const;
export type PaymentLinkStatusEnumValue = (typeof PaymentLinkStatusEnum)[keyof typeof PaymentLinkStatusEnum];

/** Filtro de tareas (scheduler) */
export const TaskFilterEnum = {
  ALL: 'ALL',
  FAILED: 'FAILED',
  RUNNING: 'RUNNING',
  SCHEDULED: 'SCHEDULED',
  SUCCEEDED: 'SUCCEEDED'
} as const;
export type TaskFilterEnumValue = (typeof TaskFilterEnum)[keyof typeof TaskFilterEnum];

/** Ordenamiento de tareas */
export const TaskSortingEnum = {
  DEFAULT: 'DEFAULT',
  NAME: 'NAME'
} as const;
export type TaskSortingEnumValue = (typeof TaskSortingEnum)[keyof typeof TaskSortingEnum];

// =============================================================================
// DTOs BASE (sin dependencias o mínimas)
// =============================================================================

export interface CountryDTO {
  id?: number;
  name?: string;
  code?: string;
}

export interface NeighborhoodDTO {
  code?: string;
  name?: string;
}

export interface PostalCodeDTO {
  zipCode?: string;
  state?: string;
  stateCode?: string;
  municipality?: string;
  municipalityCode?: string;
  city?: string;
  neighborhoods?: NeighborhoodDTO[];
}

export interface CurrencyDTO {
  id?: number;
  name?: string;
  code?: string;
}

export interface PermissionDTO {
  value?: string;
  description?: string;
  global?: boolean;
}

export interface RoleDTO {
  value?: string;
  description?: string;
  system?: boolean;
  global?: boolean;
  permissions?: PermissionDTO[];
}

export interface TaxDTO {
  id?: number;
  taxKey?: string;
  displayName?: string;
  description?: string;
  taxType?: TaxTypeEnumValue;
  rate?: number;
  validFrom?: string;
  validTo?: string;
  state?: EntityStateValue;
}

export interface MeasureUnitDTO {
  id?: number;
  code?: string;
  name?: string;
  description?: string;
  symbol?: string;
  effectiveDate?: string;
  expiryDate?: string;
  state?: EntityStateValue;
}

export interface TaxProductCodeDTO {
  id?: number;
  taxCode?: string;
  taxDescription?: string;
  effectiveDate?: string;
  expiryDate?: string;
  state?: EntityStateValue;
}

/** Régimen fiscal (API catálogo SAT / clientes) */
export interface TaxRegimeDTO {
  id?: number;
  code?: string;
  name?: string;
  description?: string;
  state?: EntityStateValue;
  canFisicalPerson?: boolean;
  canMoralPerson?: boolean;
}

// =============================================================================
// DTOs DE PRODUCTO
// =============================================================================

export interface ProductTaxDTO {
  id?: number;
  tax?: TaxDTO;
  taxRate?: number;
  taxAmount?: number;
  applicationType?: TaxApplicationTypeEnumValue;
  createdAt?: string;
  updatedAt?: string;
  state?: EntityStateValue;
}

export interface ProductDTO {
  id?: number;
  name?: string;
  description?: string;
  sku?: string;
  externalId?: string;
  unitPrice?: number;
  currency?: CurrencyDTO;
  measureUnitId?: number;
  measureUnitCode?: string;
  measureUnitName?: string;
  taxCodeId?: number;
  taxCodeTaxCode?: string;
  taxCodeDescription?: string;
  satObjetoImpId?: string;
  satObjetoImpName?: string;
  taxInclusive?: boolean;
  taxability?: string;
  state?: EntityStateValue;
  createdUserId?: number;
  lastUserId?: number;
  taxes?: ProductTaxDTO[];
  createdAt?: string;
  updatedAt?: string;
}

// =============================================================================
// DTOs DE CLIENTE / EMPRESA
// =============================================================================

export interface CustomerAddressDTO {
  id?: number;
  type?: AddressTypeEnumValue;
  country?: CountryDTO;
  municipality?: string;
  street?: string;
  colonia?: string;
  externalNo?: string;
  internalNo?: string;
  zipCode?: string;
  state?: EntityStateValue;
  createdAt?: string;
  updatedAt?: string;
}

export interface CustomerContactDTO {
  id?: number;
  type?: ContactTypeEnumValue;
  conactName?: string; // Nota: typo en API
  email?: string;
  phone?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CustomerDTO {
  id?: number;
  taxId?: string;
  taxRegime?: TaxRegimeDTO;
  createdAt?: string;
  updatedAt?: string;
  type?: CompanyTypeEnumValue;
  legalName?: string;
  comercialName?: string;
  firstName?: string;
  lastName?: string;
  country?: CountryDTO;
  email?: string;
  phone?: string;
  state?: EntityStateValue;
  addresses?: CustomerAddressDTO[];
  contacts?: CustomerContactDTO[];
  daysUntilExpiry?: number | null;
}

export interface CompanyAddressDTO {
  id?: number;
  type?: AddressTypeEnumValue;
  country?: CountryDTO;
  municipality?: string;
  street?: string;
  colonia?: string;
  externalNo?: string;
  internalNo?: string;
  zipCode?: string;
  state?: EntityStateValue;
  createdAt?: string;
  updatedAt?: string;
}

export interface CompanyContactDTO {
  id?: number;
  type?: ContactTypeEnumValue;
  conactName?: string;
  email?: string;
  phone?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type DynamicFieldType = 'TEXT' | 'NUMBER' | 'DATE' | 'BOOLEAN' | 'SELECT';

export interface DynamicField {
  id?: number;
  clientId?: number;
  fieldKey?: string;
  label?: string;
  type?: DynamicFieldType;
  required?: boolean;
  /** Si el valor del campo se imprime en la factura. Ausente equivale a false. */
  includeInInvoice?: boolean;
  displayOrder?: number;
  options?: string[];
  validationRegex?: string;
}

export interface CompanySaleConfig {
  dynamicFields?: DynamicField[];
}

export interface CompanySubscriptionConfig {
  /** Hora UTC aplicada a nextBillingDate al crear o renovar suscripciones (HH:mm:ss). */
  renewalsHourUTC?: string;
  dynamicFields?: DynamicField[];
}

export interface CompanyNotificationConfig {
  domain?: string;
  sendEmail?: string;
  endEmailAlias?: string;
  replayEmail?: string;
  replayEmailAlias?: string;
}

export interface CompanyInvoiceConfig {
  enableInvoice?: boolean;
  enableAutoInvoice?: boolean;
  companySaleConfig?: CompanySaleConfig;
  companySubscriptionConfig?: CompanySubscriptionConfig;
  companyNotificationConfig?: CompanyNotificationConfig;
}

export interface CompanyConfig {
  companyInvoiceConfig?: CompanyInvoiceConfig;
}

export interface CompanyDTO {
  id?: number;
  taxId?: string;
  taxRegime?: TaxRegimeDTO;
  createdAt?: string;
  updatedAt?: string;
  type?: CompanyTypeEnumValue;
  legalName?: string;
  comercialName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  country?: CountryDTO;
  state?: EntityStateValue;
  addresses?: CompanyAddressDTO[];
  contacts?: CompanyContactDTO[];
  logoUrl?: string;
  daysUntilExpiry?: number | null;
  companyConfig?: CompanyConfig;
}

export interface CompanySignImageDTO {
  id?: number;
  url?: string;
  name?: string;
  contentType?: string;
  size?: number;
}

export interface UploadCompanySignImageRequest {
  imageBase64: string;
  contentType?: string;
  name?: string;
}

// =============================================================================
// DTOs DE FACTURA
// =============================================================================

export interface BillingTermsDTO {
  id?: number;
  billingType?: BillingTypeEnumValue;
  /** Catálogo SAT — forma de pago (API puede incluirlo en `billingTerms` de la venta). */
  paymentForm?: PaymentFormDTO;
  /** Catálogo SAT — método de pago (API puede incluirlo en `billingTerms` de la venta). */
  paymentMethod?: PaymentMethodDTO;
  collectionMethod?: CollectionMethodEnumValue;
  /** Uso CFDI (ventas: persistido en `billing_terms`; facturas siguen con `cfdiUse` en raíz del DTO). */
  cfdiUse?: SATCFDIUseDTO;
  gracePeriodDays?: number;
  daySelection?: DaySelectionEnumValue;
  selectedDay?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface InvoiceLineTaxDTO {
  id?: number;
  tax?: TaxDTO;
  taxType?: LineTaxTypeEnumValue;
  taxRate?: number;
  taxAmount?: number;
  taxBase?: number;
  taxIncluded?: boolean;
  applicationType?: LineTaxApplicationTypeEnumValue;
  collectionType?: TaxCollectionTypeEnumValue;
}

/** Objeto de impuesto SAT (catálogo CFDI) */
export interface SATObjetoImpDTO {
  id?: string;
  name?: string;
  description?: string;
  state?: EntityStateValue;
}

export interface SATCFDIUseDTO {
  id?: string;
  name?: string;
  description?: string;
  canFisicalPerson?: boolean;
  canMoralPerson?: boolean;
  recipientTypes?: string[];
  state?: EntityStateValue;
}

export interface CfdiCancelMotivoDTO {
  name?: string;
  description?: string;
  requiresRelatedCfdi?: boolean;
  order?: number;
  active?: boolean;
}

export interface InvoiceLineDTO {
  id?: number;
  description?: string;
  quantity?: number;
  measureUnit?: MeasureUnitDTO;
  unitPrice?: number;
  subtotal?: number;
  product?: ProductDTO;
  sku?: string;
  externalId?: string;
  taxProductCode?: TaxProductCodeDTO;
  taxProductCodeCode?: string;
  taxProductCodeDescription?: string;
  satObjetoImp?: SATObjetoImpDTO;
  taxExempt?: boolean;
  taxBehavior?: TaxBehaviorEnumValue;
  taxInclusive?: boolean;
  taxAmount?: number;
  totalAmount?: number;
  taxes?: InvoiceLineTaxDTO[];
  metadata?: Record<string, unknown>;
}

export interface InvoiceDTO {
  id?: number;
  saleId?: number;
  saleNumber?: string;
  invoiceId?: string;
  providerId?: string;
  issueDate?: string;
  folio?: string;
  serie?: string;
  pdfFileId?: number;
  xmlFileId?: number;
  state?: EntityStateValue;
  customer?: CustomerDTO;
  billingTerms?: BillingTermsDTO;
  number?: string;
  series?: string;
  isStamped?: boolean;
  paymentForm?: PaymentFormDTO;
  paymentMethod?: PaymentMethodDTO;
  cfdiUse?: SATCFDIUseDTO;
  status?: InvoiceStatusEnumValue | CfdiInvoiceStatusEnumValue;
  cancelMotivo?: string;
  relatedInvoice?: InvoiceDTO;
  delinquencyStatus?: DelinquencyStatusEnumValue;
  collectionStartDate?: string;
  daysUntilExpiry?: number;
  penaltyFeeAmount?: number;
  penaltyInterestRate?: number;
  numberOfPayments?: number;
  frequency?: FrequencyEnumValue;
  createdAt?: string;
  paidAt?: string;
  voidedAt?: string;
  subtotal?: number;
  tax?: number;
  discount?: number;
  total?: number;
  amountDue?: number;
  currency?: string;
  notes?: string;
  lines?: InvoiceLineDTO[];
}

/** Línea de venta (API /sales) */
export interface SaleLineTaxDTO {
  id?: number;
  tax?: TaxDTO;
  taxType?: LineTaxTypeEnumValue;
  taxRate?: number;
  taxAmount?: number;
  taxBase?: number;
  taxIncluded?: boolean;
  applicationType?: LineTaxApplicationTypeEnumValue;
  collectionType?: TaxCollectionTypeEnumValue;
}

export interface SaleLineDTO {
  id?: number;
  /** SHA-256 de campos que mueven dinero (backend). */
  hash?: string;
  description?: string;
  quantity?: number;
  measureUnit?: MeasureUnitDTO;
  unitPrice?: number;
  subtotal?: number;
  product?: ProductDTO;
  sku?: string;
  externalId?: string;
  taxProductCode?: TaxProductCodeDTO;
  taxProductCodeCode?: string;
  taxProductCodeDescription?: string;
  satObjetoImp?: SATObjetoImpDTO;
  taxExempt?: boolean;
  taxBehavior?: TaxBehaviorEnumValue;
  taxInclusive?: boolean;
  taxAmount?: number;
  totalAmount?: number;
  taxes?: SaleLineTaxDTO[];
  metadata?: Record<string, unknown>;
}

/** Venta (API `GET/POST/PUT /sales`) */
export interface SaleDTO {
  id?: number;
  /** Si la venta tiene factura fiscal vinculada (API puede exponer `invoiceId` o `invoice.id`). */
  invoiceId?: number;
  invoice?: InvoiceDTO;
  customer?: CustomerDTO;
  billingTerms?: BillingTermsDTO;
  number?: string;
  /** Referencia única de conciliación bancaria (Crockford). Solo lectura en UI. */
  paymentReference?: string;
  isStamped?: boolean;
  autoInvoice?: boolean;
  /** Nivel de edición calculado por el backend. */
  editLevel?: 'ADDITIONAL_ONLY' | 'FISCAL_DATA' | 'FULL';
  collectionMethod?: CollectionMethodEnumValue;
  processStatus?: SalePipelineStatusEnumValue;
  status?: InvoiceStatusEnumValue;
  state?: EntityStateValue;
  saleType?: SaleTypeEnumValue;
  delinquencyStatus?: DelinquencyStatusEnumValue;
  collectionStartDate?: string;
  dateOfSale?: string;
  daysUntilExpiry?: number;
  penaltyFeeAmount?: number;
  penaltyInterestRate?: number;
  numberOfPayments?: number;
  frequency?: FrequencyEnumValue;
  createdAt?: string;
  issueDate?: string;
  paidAt?: string;
  voidedAt?: string;
  cancelReason?: string;
  subtotal?: number;
  tax?: number;
  discount?: number;
  total?: number;
  amountDue?: number;
  currency?: string;
  notes?: string;
  lines?: SaleLineDTO[];
  /** Valores de campos dinámicos de la empresa. */
  additionalFields?: Record<string, unknown>;
  /** Suscripción vinculada (primer nivel; puede ser null). */
  subscription?: SubscriptionDTO;
}

// =============================================================================
// DTOs DE PLAN
// =============================================================================

export interface PlanItemTaxDTO {
  id?: number;
  tax?: TaxDTO;
  taxType?: LineTaxTypeEnumValue;
  taxRate?: number;
  taxAmount?: number;
  taxBase?: number;
  taxIncluded?: boolean;
  applicationType?: LineTaxApplicationTypeEnumValue;
  collectionType?: TaxCollectionTypeEnumValue;
}

export interface PlanItemDTO {
  id?: number;
  description?: string;
  quantity?: number;
  measureUnit?: MeasureUnitDTO;
  unitPrice?: number;
  subtotal?: number;
  product?: ProductDTO;
  sku?: string;
  externalId?: string;
  taxProductCode?: TaxProductCodeDTO;
  taxProductCodeCode?: string;
  taxProductCodeDescription?: string;
  satObjetoImp?: SATObjetoImpDTO;
  taxExempt?: boolean;
  taxInclusive?: boolean;
  taxAmount?: number;
  totalAmount?: number;
  taxes?: PlanItemTaxDTO[];
  metadata?: Record<string, unknown>;
}

export interface PlanDTO {
  id?: number;
  name?: string;
  description?: string;
  currency?: string;
  frequency?: FrequencyEnumValue;
  status?: PlanStatusEnumValue;
  subtotal?: number;
  tax?: number;
  discount?: number;
  total?: number;
  createdAt?: string;
  updatedAt?: string;
  items?: PlanItemDTO[];
  metadata?: Record<string, unknown>;
}

// =============================================================================
// DTOs DE SUSCRIPCIÓN
// =============================================================================

export const SubscriptionStatusEnum = {
  PENDING_PAYMENT: 'PENDING_PAYMENT',
  TRAILING: 'TRAILING',
  ACTIVE: 'ACTIVE',
  PAST_DUE: 'PAST_DUE',
  CANCELED: 'CANCELED',
  FINISHED: 'FINISHED',
  /** @deprecated Preferir CANCELED (alineado con API). */
  CANCELLED: 'CANCELLED',
  /** @deprecated Legacy UI. */
  PAUSED: 'PAUSED',
  /** @deprecated Preferir FINISHED (alineado con API). */
  EXPIRED: 'EXPIRED'
} as const;
export type SubscriptionStatusEnumValue =
  (typeof SubscriptionStatusEnum)[keyof typeof SubscriptionStatusEnum];

export interface SubscriptionDTO {
  id?: number;
  /** Folio interno (ej. S1740000000000). */
  number?: string;
  /** SHA-256 de moneda, totales e items (backend). */
  hash?: string;
  token?: string;
  plan?: PlanDTO;
  customer?: CustomerDTO;
  status?: SubscriptionStatusEnumValue;
  state?: EntityStateValue;
  /** Fuente de verdad del método de cobro (como en Sale). */
  collectionMethod?: CollectionMethodEnumValue;
  frequency?: FrequencyEnumValue;
  currency?: string;
  subtotal?: number;
  tax?: number;
  discount?: number;
  total?: number;
  customerName?: string;
  customerEmail?: string;
  startDate?: string;
  nextBillingDate?: string;
  /** Fecha a partir de la cual se crea la primera venta. */
  collectionStartDate?: string;
  /** Día del mes (1–31) de renovación. */
  billingDayOfMonth?: number | null;
  /** Plazo de pago en días para ventas/cargos generados. */
  daysUntilExpiry?: number | null;
  /** Generar factura automáticamente al recibir el pago. */
  autoInvoice?: boolean;
  endDate?: string;
  createdAt?: string;
  updatedAt?: string;
  metadata?: Record<string, unknown>;
  /** Valores de campos dinámicos de la empresa. */
  additionalFields?: Record<string, unknown>;
  /** Notas que se propagan a ventas/recibos generados. */
  notes?: string;
  /**
   * Reto 3D Secure pendiente. Cuando viene poblado, la suscripción aún no está
   * activa: el frontend debe correr el reto con la SDK del proveedor y llamar a
   * {@code confirm-3ds} para completar el cobro.
   */
  threeDSChallenge?: ThreeDSChallengeDTO;
  billingTerms?: BillingTermsDTO;
  /** Ítems de la suscripción (misma forma que líneas de venta). */
  lines?: SaleLineDTO[];
}

/**
 * Datos del reto 3D Secure que el frontend debe ejecutar antes de confirmar el cobro.
 * Coincide con {@code ThreeDSChallengeDTO} del backend.
 */
export interface ThreeDSChallengeDTO {
  chargeStatus: string;
  transactionTokenId: string;
  responseCode: number;
  status: string;
  authUrl: string;
  acsUrl: string;
  paReq: string;
  authenticationTransactionID: string;
}

/**
 * Alta de suscripción: misma estructura que {@link CreateSaleRequest} + `planId` opcional.
 * Si viene `planId`, frequency/currency/montos/lines se toman del plan.
 * Si no, esos campos comerciales son obligatorios en el request.
 */
export interface CreateSubscriptionRequest {
  id?: number;
  planId?: number;
  customerId?: number;
  newCustomer?: CreateCustomerRequest;
  billingTerms: CreateSaleBillingTerms;
  /** Folio (API: `number`). En alta puede omitirse. */
  number?: string;
  paymentFormId?: number;
  paymentMethodId?: number;
  status: InvoiceStatusEnumValue;
  collectionStartDate?: string;
  /** Día del mes (1–31) de renovación. */
  billingDayOfMonth?: number;
  daysUntilExpiry?: number;
  /** Generar factura automáticamente al recibir el pago. */
  autoInvoice?: boolean;
  penaltyFeeAmount?: number;
  penaltyInterestRate?: number;
  /** Obligatorio si no hay `planId`. */
  frequency?: FrequencyEnumValue;
  /** Obligatorio si no hay `planId`. */
  subtotal?: number;
  /** Obligatorio si no hay `planId`. */
  tax?: number;
  /** Obligatorio si no hay `planId`. */
  discount?: number;
  /** Obligatorio si no hay `planId`. */
  total?: number;
  /** Obligatorio si no hay `planId`. */
  currencyId?: number;
  notes?: string;
  /** Obligatorio (no vacío) si no hay `planId`. */
  lines?: CreateSaleLineRequest[];
  metadata?: Record<string, unknown>;
  /** Valores de campos dinámicos de la empresa. */
  additionalFields?: Record<string, unknown>;
  /** Agrupa los meses vencidos en una sola venta al crear la suscripción. */
  groupCatchUpCycles?: boolean;
}

/**
 * Actualización completa de suscripción (PUT). No incluye ni modifica `status`.
 * Si viene `planId`, frequency/currency/montos/lines se toman del plan.
 * Si no, esos campos comerciales son obligatorios.
 */
export interface UpdateSubscriptionRequest {
  planId?: number;
  billingTerms: CreateSaleBillingTerms;
  /** Folio existente; si se omite se conserva el actual. */
  number?: string;
  paymentFormId?: number;
  paymentMethodId?: number;
  /** Fecha a partir de la cual se crea la primera venta. */
  collectionStartDate?: string;
  /** Día del mes (1–31) de renovación. */
  billingDayOfMonth?: number;
  daysUntilExpiry?: number;
  /** Generar factura automáticamente al recibir el pago. */
  autoInvoice?: boolean;
  /** Obligatorio si no hay `planId`. */
  frequency?: FrequencyEnumValue;
  /**
   * Obligatorio si cambia la frecuencia, o el día de cobro en mensual/trimestral/anual,
   * de una suscripción con ciclo activo.
   */
  billingCycleChangeMode?: BillingCycleChangeModeValue;
  /** Obligatorio si no hay `planId`. */
  subtotal?: number;
  /** Obligatorio si no hay `planId`. */
  tax?: number;
  /** Obligatorio si no hay `planId`. */
  discount?: number;
  /** Obligatorio si no hay `planId`. */
  total?: number;
  /** Obligatorio si no hay `planId`. */
  currencyId?: number;
  notes?: string;
  /** Obligatorio (no vacío) si no hay `planId`. */
  lines?: CreateSaleLineRequest[];
  metadata?: Record<string, unknown>;
  /** Valores de campos dinámicos de la empresa. */
  additionalFields?: Record<string, unknown>;
}

/**
 * Cómo aplicar un cambio de frecuencia o día de cobro en una suscripción con ciclo activo.
 * `CORRECT_CURRENT_PERIOD`: la venta del periodo vigente se toma como el primer ciclo con la nueva configuración.
 * `NEXT_CYCLE`: el cambio aplica a partir de la próxima renovación.
 * `ADJUST_CURRENT_PERIOD`: el nuevo plan aplica desde el periodo actual; su cobro sin pagar se cancela y se reemite.
 */
export type BillingCycleChangeModeValue = 'CORRECT_CURRENT_PERIOD' | 'NEXT_CYCLE' | 'ADJUST_CURRENT_PERIOD';

export interface PublicSubscriptionCheckoutRequest {
  customerName: string;
  /** Apellido paterno (requerido en checkout Paycode). */
  customerMiddleName?: string;
  /** Apellido materno (opcional; checkout Paycode). */
  customerLastName?: string;
  customerEmail: string;
  /** Teléfono del cliente (requerido en checkout Paycode; 10–15 dígitos). */
  customerPhone?: string;
  /** ID de tarjeta guardada del cliente (alternativa a cardToken). */
  customerCardId?: number;
  /** Token / hash generado en el cliente (NetPay). */
  cardToken?: string;
  /** Número de tarjeta (checkout Paycode; sin espacios). */
  cardNumber?: string;
  /** Nombre del titular (checkout Paycode). */
  cardholderName?: string;
  /** Mes de expiración MM (checkout Paycode). */
  expMonth?: string;
  /** Año de expiración YYYY (checkout Paycode). */
  expYear?: string;
  /** CVV: se envía al API para el cobro; no se reutiliza el del flujo de tokenización en el payload de contacto. */
  cvv?: string;
  /** Respuesta del proveedor al crear el token (opcional; NetPay). */
  tokenProviderPayload?: Record<string, unknown>;
  /** referenceId de NetPay 3DS (página pública de suscripción). */
  referenceId?: string;
  /** User-Agent del navegador en el checkout. */
  userAgent?: string;
  /** `NetPay.form.deviceInformation()` en el cliente (3DS / antifraude). */
  deviceInformation?: Record<string, string>;
  /** `NetPay.form.generateDeviceFingerPrint()` al cargar (antifraude / 3DS). */
  deviceFingerPrint?: string;
  /** Uso de CFDI (si el cliente solicita factura). */
  cfdiUseId?: string;
}

export interface CustomerCardDTO {
  id?: number;
  lastDigits?: string;
  expirationMonth?: string;
  expirationYear?: string;
  brand?: string;
  bank?: string;
  type?: string;
  paymentProvider?: PaymentProviderEnumValue;
  validated?: boolean;
  state?: EntityStateValue;
  createdAt?: string;
  updatedAt?: string;
}

export interface PublicSubscriptionDetailDTO {
  subscription?: SubscriptionDTO;
  companyName?: string;
  companyLogoUrl?: string;
  /** Tarjetas del cliente filtradas por paymentProvider de la suscripción. */
  customerCards?: CustomerCardDTO[];
  /** Proveedor de pago de la compañía (NETPAY | PAYCODE). */
  provider?: PaymentProviderEnumValue;
}

/**
 * Cuerpo del endpoint público {@code POST /public/subscriptions/{token}/confirm-3ds}.
 * Se invoca después del challenge 3DS exitoso para que el backend confirme la
 * transacción contra NetPay y active la suscripción.
 */
export interface PublicSubscriptionConfirm3DSRequest {
  /**
   * Id devuelto por el ACS al callback de {@code netpay3ds.proceed(...)}. Si
   * {@code canProceed} resolvió {@code false} en el cliente, mandar {@code null}
   * para que NetPay confirme sin 3DS.
   */
  processorTransactionId?: string | null;
}

export interface AdvanceBillingCyclesRequest {
  cycles: number;
  /** true = una venta con montos × N; false = N ventas. */
  groupCycles: boolean;
}

export interface AdvanceBillingCycleSaleSummaryDTO {
  saleId?: number;
  saleNumber?: string;
  cycleIndex?: number | null;
  cyclesIncluded?: number;
  billingDate?: string;
  subtotal?: number;
  tax?: number;
  discount?: number;
  total?: number;
  currency?: string;
}

export interface AdvanceBillingCyclesResultDTO {
  subscriptionId?: number;
  cyclesAdvanced?: number;
  groupCycles?: boolean;
  previousNextBillingDate?: string;
  nextBillingDate?: string;
  sales?: AdvanceBillingCycleSaleSummaryDTO[];
}

export interface WrapperResponseSubscriptionDTO extends WrapperResponse<SubscriptionDTO> {}
export interface WrapperResponseListSubscriptionDTO extends WrapperResponse<SubscriptionDTO[]> {}
export interface WrapperPageableResponseSubscriptionDTO extends WrapperPageableResponse<SubscriptionDTO> {}
export interface WrapperResponsePublicSubscriptionDetailDTO extends WrapperResponse<PublicSubscriptionDetailDTO> {}
export interface WrapperResponseAdvanceBillingCyclesResultDTO
  extends WrapperResponse<AdvanceBillingCyclesResultDTO> {}

export type SearchSubscriptionFilterSpec = Record<string, unknown>;
export type ListSubscriptionFilterSpec = Record<string, unknown>;

// =============================================================================

export interface PaymentFormDTO {
  id?: number;
  code?: string;
  country?: CountryDTO;
  name?: string;
  description?: string;
  effectiveDate?: string;
  expiryDate?: string;
  state?: EntityStateValue;
}

export interface PaymentMethodDTO {
  id?: number;
  code?: string;
  country?: CountryDTO;
  name?: string;
  description?: string;
  effectiveDate?: string;
  expiryDate?: string;
  state?: EntityStateValue;
}

// =============================================================================
// DTOs DE USUARIO Y PAGO
// =============================================================================

export interface UserDTO {
  id?: number;
  email?: string;
  type?: UserTypeValue;
  name?: string;
  lastname?: string;
  surname?: string;
  phone?: string;
  jobTitle?: string;
  avatarUrl?: string;
  signatureId?: number | null;
  signatureName?: string | null;
  createdUserId?: number;
  lastUserId?: number;
  time?: string;
  state?: EntityStateValue;
  createdAt?: string;
  updatedAt?: string;
  permissions?: PermissionEnumValue[];
  roles?: RoleEnumValue[];
}

export interface PaymentLinkDTO {
  id?: number;
  customer?: CustomerDTO;
  expiresAt?: string;
  link?: string;
  concept?: string;
  provider?: PaymentLinkProviderEnumValue;
  providerId?: string;
  trackingId?: string;
  totalAmount?: number;
  redirectUrl?: string;
  paidAt?: string;
  status?: PaymentLinkStatusEnumValue;
  createdAt?: string;
  updatedAt?: string;
}

export interface ListViewDTO {
  id?: number;
  code?: string;
  label?: string;
  description?: string;
}

// =============================================================================
// REQUEST: Usuario
// =============================================================================

export interface CreateUserRequest {
  email: string;
  name: string;
  lastname?: string;
  surname?: string;
  phone?: string;
  jobTitle?: string;
  password: string;
  permissions?: PermissionEnumValue[];
  roles?: RoleEnumValue[];
}

export interface UpdateUserRequest {
  name?: string;
  lastname?: string;
  surname?: string;
  phone?: string;
  jobTitle?: string;
  state: EntityStateValue;
  permissions?: PermissionEnumValue[];
  roles?: RoleEnumValue[];
}

// =============================================================================
// REQUEST: Producto
// =============================================================================

export interface CreateProductTaxRequest {
  taxId: number;
  taxRate?: number;
  taxAmount?: number;
  applicationType: TaxApplicationTypeEnumValue;
}

export interface CreateProductRequest {
  name?: string;
  description?: string;
  sku?: string;
  externalId?: string;
  unitPrice?: number;
  currencyId: number;
  measureUnitId: number;
  taxCodeId: number;
  taxInclusive?: boolean;
  state?: EntityStateValue;
  metadata?: Record<string, unknown>;
  taxes?: CreateProductTaxRequest[];
}

export interface UpdateProductRequest {
  name?: string;
  description?: string;
  sku?: string;
  externalId?: string;
  unitPrice?: number;
  currencyId: number;
  measureUnitId: number;
  taxCodeId: number;
  taxInclusive?: boolean;
  state?: EntityStateValue;
  metadata?: Record<string, unknown>;
}

export interface UpdateProductTaxRequest {
  taxId: number;
  taxRate?: number;
  taxAmount?: number;
  applicationType: TaxApplicationTypeEnumValue;
}

// =============================================================================
// REQUEST: Cliente
// =============================================================================

export interface CreateCustomerAddressRequest {
  type: AddressTypeEnumValue;
  country: string;
  street: string;
  municipality?: string;
  colonia?: string;
  externalNo?: string;
  internalNo?: string;
  zipCode?: string;
}

export interface CreateCustomerContactRequest {
  type: ContactTypeEnumValue;
  conactName: string;
  email?: string;
  phone?: string;
}

export interface CreateCustomerRequest {
  type?: CompanyTypeEnumValue | null;
  taxRegimeId?: number | null;
  taxId?: string | null;
  legalName: string;
  comercialName: string;
  countryId?: number | null;
  email: string;
  phone?: string;
  state?: EntityStateValue;
  addresses?: CreateCustomerAddressRequest[];
  contacts?: CreateCustomerContactRequest[];
  daysUntilExpiry?: number | null;
}

export interface UpdateCustomerRequest {
  type?: CompanyTypeEnumValue | null;
  taxRegimeId?: number | null;
  taxId?: string | null;
  legalName: string;
  comercialName: string;
  countryId?: number | null;
  email: string;
  phone?: string;
  daysUntilExpiry?: number | null;
  state?: EntityStateValue;
}

// =============================================================================
// REQUEST: Empresa
// =============================================================================

export interface CreateCompanyAddressRequest {
  type: AddressTypeEnumValue;
  country: string;
  street: string;
  municipality?: string;
  colonia?: string;
  externalNo?: string;
  internalNo?: string;
  zipCode?: string;
}

export interface CreateCompanyContactRequest {
  type: ContactTypeEnumValue;
  conactName: string;
  email?: string;
  phone?: string;
}

export interface CreateCompanyRequest {
  type: CompanyTypeEnumValue;
  taxRegimeId: number;
  taxId: string;
  legalName: string;
  comercialName: string;
  firstName?: string;
  lastName?: string;
  country: string;
  email: string;
  phone: string;
  state?: EntityStateValue;
  addresses?: CreateCompanyAddressRequest[];
  contacts?: CreateCompanyContactRequest[];
  logoBase64?: string;
  logoContentType?: string;
  daysUntilExpiry?: number | null;
}

/** Guardado de toda la página de configuración en una transacción; las secciones de plataforma son opcionales. */
export interface UpdateCompanyConfigRequest {
  companyConfig: CompanyConfig;
  /** Solo con `MIT_CONFIG_WRITE`. */
  mitConfig?: UpdateMitConfigRequest;
  /** Solo con `CUSTOMER_NOTIFICATION_CONFIG_WRITE`. */
  customerNotificationConfig?: UpdateCustomerNotificationConfigRequest;
}

// =============================================================================
// REQUEST: Factura
// =============================================================================

export interface CreateInvoiceBillingTerms {
  billingType: BillingTypeEnumValue;
  collectionMethod?: CollectionMethodEnumValue;
  gracePeriodDays?: number;
  daySelection?: DaySelectionEnumValue;
  selectedDay?: number;
}

export interface CreateInvoiceLineTaxRequest {
  taxCode?: string;
  taxRate?: number;
  taxAmount?: number;
  taxBase?: number;
  applicationType?: LineTaxApplicationTypeEnumValue;
  collectionType?: TaxCollectionTypeEnumValue;
}

export interface CreateInvoiceLineRequest {
  productId?: number;
  newProduct?: CreateProductRequest;
  description?: string;
  measureUnitId?: number;
  taxProductCodeId?: number;
  satObjetoImpId?: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  taxAmount?: number;
  totalAmount: number;
  externalId?: string;
  taxExempt?: boolean;
  taxInclusive?: boolean;
  taxes?: CreateInvoiceLineTaxRequest[];
  metadata?: Record<string, unknown>;
}

/** Request para el preview de una línea de factura: calcula impuestos y totales sin persistencia. */
export interface PreviewInvoiceLineRequest {
  subtotal: number;
  taxInclusive?: boolean;
  taxes?: CreateInvoiceLineTaxRequest[];
}

export interface CreateInvoiceRequest {
  id?: number;
  customerId?: number;
  newCustomer?: CreateCustomerRequest;
  billingTerms: CreateInvoiceBillingTerms;
  number: string;
  series?: string;
  paymentFormId?: number;
  paymentMethodId?: number;
  cfdiUseId?: string;
  status: InvoiceStatusEnumValue;
  collectionStartDate?: string;
  daysUntilExpiry?: number;
  penaltyFeeAmount?: number;
  penaltyInterestRate?: number;
  numberOfPayments?: number;
  frequency?: FrequencyEnumValue;
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  currencyId: number;
  notes?: string;
  lines?: CreateInvoiceLineRequest[];
  metadata?: Record<string, unknown>;
}

export interface CreateSaleBillingTerms {
  billingType?: BillingTypeEnumValue;
  collectionMethod?: CollectionMethodEnumValue;
  cfdiUseId?: string;
  daySelection?: DaySelectionEnumValue;
  selectedDay?: number;
}

export interface CreateSaleLineTaxRequest {
  taxCode?: string;
  taxRate?: number;
  taxAmount?: number;
  taxBase?: number;
  applicationType?: LineTaxApplicationTypeEnumValue;
  collectionType?: TaxCollectionTypeEnumValue;
}

export interface CreateSaleLineRequest {
  id?: number;
  productId?: number;
  newProduct?: CreateProductRequest;
  description?: string;
  measureUnitId?: number;
  taxProductCodeId?: number;
  satObjetoImpId?: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  taxAmount?: number;
  totalAmount: number;
  externalId?: string;
  taxExempt?: boolean;
  taxInclusive?: boolean;
  taxes?: CreateSaleLineTaxRequest[];
  metadata?: Record<string, unknown>;
}

export interface PreviewSaleLineRequest {
  subtotal: number;
  taxInclusive?: boolean;
  taxes?: CreateSaleLineTaxRequest[];
}

/** PUT /sales/{id}/invoicing — perfil fiscal sin tocar estrategia de emisión ni resto de la venta. */
export interface UpdateSaleInvoicingProfileRequest {
  paymentFormId?: number | null;
  paymentMethodId?: number | null;
  cfdiUseId?: string | null;
  autoInvoice?: boolean | null;
}

export interface CreateSaleRequest {
  id?: number;
  /** Si se indica, la venta se asocia a esa suscripción y el API asigna `saleType=SUBSCRIPTION`. */
  subscriptionId?: number;
  customerId?: number;
  newCustomer?: CreateCustomerRequest;
  billingTerms: CreateSaleBillingTerms;
  paymentFormId?: number;
  paymentMethodId?: number;
  /** Embudo comercial (`Sale.processStatus` / `SaleProcessStatusEnum`) */
  processStatus: SalePipelineStatusEnumValue;
  /** Estado del ciclo de cobro (API OpenAPI: alineado a `InvoiceStatus`) */
  status: InvoiceStatusEnumValue;
  collectionStartDate?: string;
  dateOfSale?: string;
  daysUntilExpiry?: number;
  penaltyFeeAmount?: number;
  penaltyInterestRate?: number;
  numberOfPayments?: number;
  frequency?: FrequencyEnumValue;
  /** Generar factura automáticamente al recibir el pago. */
  autoInvoice?: boolean;
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  currencyId: number;
  notes?: string;
  /** Null = no modificar líneas. Lista vacía = borrar todas. */
  lines?: CreateSaleLineRequest[] | null;
  metadata?: Record<string, unknown>;
  /** Valores de campos dinámicos de la empresa. */
  additionalFields?: Record<string, unknown>;
}

// =============================================================================
// REQUEST: Plan
// =============================================================================

export interface CreatePlanRequest {
  id?: number;
  name: string;
  description?: string;
  currencyId: number;
  frequency: FrequencyEnumValue;
  status: PlanStatusEnumValue;
  discount?: number;
  items?: CreateSaleLineRequest[];
  metadata?: Record<string, unknown>;
}

// =============================================================================
// DTO: Archivo / Documento timbrado
// =============================================================================

/** Respuesta del API para descargar PDF/XML timbrado. El campo `content` puede contener una URL firmada para descargar el documento. */
export interface ReadFileResponse {
  id?: number;
  name?: string;
  contentType?: string;
  /** URL de descarga o contenido del archivo (base64 según el caso) */
  content?: string;
  size?: number;
  createdAt?: string;
}

export interface AttachCustomerFileRequest {
  name: string;
  contentType?: string;
  contentBase64: string;
}

export interface AttachSaleFileRequest {
  name: string;
  contentType?: string;
  contentBase64: string;
}

export interface AttachCommunicationTemplateFileRequest {
  name: string;
  contentType?: string;
  contentBase64: string;
}

export interface EmailAttachmentMetadata {
  name?: string;
  extension?: string;
  contentType?: string;
  size?: number;
  fileId?: number;
}

// =============================================================================
// DTO: CFDI emitido
// =============================================================================

/** Respuesta al timbrar una factura (crear CFDI) */
export interface CFDIIssueResponse {
  uuid?: string;
  pdf?: string;
  xml?: string;
}

// =============================================================================
// DTO: Pago (OpenAPI `PaymentDTO`; nombre distinto de `payment-link.service` local)
// =============================================================================

export interface PaymentRecordDTO {
  id?: number;
  chargeId?: number;
  customer?: CustomerDTO;
  sale?: SaleDTO;
  paymentProvider?: PaymentProviderEnumValue;
  amount?: number;
  amountFee?: number;
  amountNet?: number;
  currency?: CurrencyDTO;
  status?: PaymentRecordStatusEnumValue;
  paymentForm?: PaymentFormDTO;
  bank?: string;
  providerId?: string;
  resultMessage?: string;
  metadata?: Record<string, unknown>;
  paidAt?: string;
  createdAt?: string;
  updatedAt?: string;
  cancelReason?: string;
}

// =============================================================================
// DTO: Resumen de cargos (API `ChargeSummaryDTO`)
// =============================================================================

export interface ChargeSummaryDTO {
  count?: number;
}

// =============================================================================
// DTO: Board / panel de tablero (API `BoardDTO`)
// =============================================================================

export type BoardType = 'COLLECTIONS';
export type BoardScope = 'GLOBAL' | 'PERSONAL';

export interface BoardConfigCustomerRef {
  id?: number;
  label?: string;
}

/** Documento versionado (V1+). */
export interface BoardConfigDocument {
  version?: number;
  customers?: BoardConfigCustomerRef[];
  collectionMethods?: string[];
  statuses?: string[];
  overdueDaysFrom?: number | null;
  overdueDaysTo?: number | null;
  dueInDaysFrom?: number | null;
  dueInDaysTo?: number | null;
  attemptCountMin?: number | null;
  cxcEmail?: boolean | null;
  cxcEmailOpened?: boolean | null;
  cxcInternal?: boolean | null;
  lastSentEmailDaysFrom?: number | null;
  lastSentEmailDaysTo?: number | null;
  lastSentInternalDaysFrom?: number | null;
  lastSentInternalDaysTo?: number | null;
  saleFrequencies?: string[];
  saleStatuses?: string[];
  saleTypes?: string[];
  [key: string]: unknown;
}

export interface BoardDTO {
  id?: number;
  title?: string;
  type?: BoardType;
  scope?: BoardScope;
  ownerUserId?: number | null;
  createdByUserId?: number | null;
  configVersion?: number;
  config?: BoardConfigDocument;
  sortOrder?: number;
  state?: EntityStateValue;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateBoardRequest {
  title: string;
  type: BoardType;
  scope: BoardScope;
  sortOrder?: number;
  config: BoardConfigDocument;
}

export interface UpdateBoardRequest {
  title: string;
  type: BoardType;
  scope: BoardScope;
  sortOrder?: number;
  config: BoardConfigDocument;
}

// =============================================================================
// DTO: Cargo (API `ChargeDTO`)
// =============================================================================

/** Seguimiento de cobranza CxC (API `CxcDTO`) */
export interface CxcDTO {
  id?: number;
  email?: boolean;
  lastSentEmail?: string;
  internal?: boolean;
  lastSentInternal?: string;
  emailOpened?: boolean;
  emailOpenedAt?: string;
  commitmentDate?: string;
}

export interface ChargeDTO {
  id?: number;
  saleId?: number;
  sale?: SaleDTO;
  invoiceId?: number;
  invoice?: InvoiceDTO;
  customerId?: number;
  collectionMethod?: CollectionMethodEnumValue;
  subtotal?: number;
  tax?: number;
  discount?: number;
  total?: number;
  notes?: string;
  numberOfPayments?: number;
  paymentNumber?: number;
  collectionStartDate?: string;
  gracePeriodDays?: number;
  dueDate?: string;
  gracePeriodEndDate?: string;
  status?: ChargeOperationalStatusEnumValue;
  metadata?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  paymentLink?: PaymentLinkDTO;
  payment?: PaymentRecordDTO;
  paidAt?: string;
  state?: EntityStateValue;
  /** Seguimiento de cobranza; ausente si el cargo no tiene historial CxC. */
  cxc?: CxcDTO | null;
}

/** Intento de cobro de un cargo (API `PaymentAttemptDTO`) */
export interface PaymentAttemptDTO {
  id?: number;
  paymentForm?: PaymentFormDTO;
  status?: PaymentRecordStatusEnumValue;
  /** `Payment.providerId` cuando el intento tiene pago asociado. */
  providerId?: string;
  /** `Payment.amount` cuando el intento tiene pago asociado. */
  amount?: number;
  resultMessage?: string;
  cancelReason?: string;
  attemptedAt?: string;
}

/** Estado de un intento de cobro en su historial (API `CollectionAttemptStatus`). */
export type CollectionAttemptStatusValue =
  | 'PROCESSING'
  | 'APPROVED'
  | 'DECLINED'
  | 'REJECTED'
  | 'PENDING_RECONCILIATION'
  | 'VOID_PENDING'
  | 'VOIDED'
  | 'VOID_FAILED'
  | 'PENDING'
  | 'EXPIRED'
  | 'CANCELLED';

export type CollectionAttemptEventTypeValue =
  | 'CREATED'
  | 'PROVIDER_REQUESTED'
  | 'PROVIDER_RESPONDED'
  | 'PROVIDER_NOTIFIED'
  | 'MANUAL_RECORDED'
  | 'MANUAL_UPDATED'
  | 'PAYMENT_CANCELLED'
  | 'RESOLVED_MANUALLY'
  | 'MARKED_STALE'
  | 'MATERIALIZED'
  | 'MATERIALIZATION_FAILED'
  | 'VOID_REQUESTED'
  | 'VOID_RESPONDED'
  | 'VOIDED'
  | 'VOID_FAILED'
  | 'MIGRATED';

export type CollectionAttemptActorTypeValue = 'SYSTEM' | 'USER' | 'PROVIDER' | 'MIGRATION';

/** Evento del historial de un intento de cobro (API `CollectionAttemptEventDTO`). */
export interface CollectionAttemptEventDTO {
  id: number;
  eventType: CollectionAttemptEventTypeValue;
  occurredAt: string;
  fromStatus?: CollectionAttemptStatusValue | null;
  toStatus?: CollectionAttemptStatusValue | null;
  actorType: CollectionAttemptActorTypeValue;
  actorName?: string | null;
  providerCode?: string | null;
  providerMessage?: string | null;
  /** `previous` en ediciones de pago manual; `outcome` y `note` en resoluciones; `error` en fallas de materialización. */
  details?: Record<string, unknown>;
}

// =============================================================================
// WEBHOOK
// =============================================================================

export interface LinkPaymentWebhookMessage {
  hash?: string;
  paid_at?: string;
  amount_paid?: string;
  status?: string;
  type_paid?: string;
}

// =============================================================================
// PAGEABLE / PAGINACIÓN
// =============================================================================

export interface Pageable {
  page?: number;
  size?: number;
  sort?: string[];
}

export interface Page {
  totalItems?: number;
  currentPage?: number;
  totalPages?: number;
  pageSize?: number;
}

// =============================================================================
// WRAPPER RESPONSES (genéricos y concretos)
// =============================================================================

export interface EntityChangeDTO {
  author?: string;
  commitId?: string;
  commitDate?: string;
  operation?: string;
  entityType?: string;
  entityId?: number;
  version?: number;
  document?: Record<string, unknown>;
}

export interface EntityPropertyDiffDTO {
  property?: string;
  left?: unknown;
  right?: unknown;
  changeType?: 'ADDED' | 'REMOVED' | 'UPDATED' | string;
}

export interface EntityAuditCompareDTO {
  author?: string;
  commitId?: string;
  previousCommitId?: string;
  nextCommitId?: string;
  commitDate?: string;
  operation?: string;
  version?: number;
  document?: Record<string, unknown>;
  previousDocument?: Record<string, unknown>;
  changes?: EntityPropertyDiffDTO[];
}

export interface WrapperResponse<T = unknown> {
  ok?: boolean;
  code?: number;
  message?: string;
  metadata?: Record<string, unknown>;
  body?: T;
}

export interface WrapperResponseListEntityChangeDTO extends WrapperResponse<EntityChangeDTO[]> {}
export interface WrapperPageableResponseEntityChangeDTO extends WrapperPageableResponse<EntityChangeDTO> {}
export interface WrapperResponseEntityAuditCompareDTO extends WrapperResponse<EntityAuditCompareDTO> {}

export interface WrapperResponseUserDTO extends WrapperResponse<UserDTO> {}
export interface WrapperResponseProductDTO extends WrapperResponse<ProductDTO> {}
export interface WrapperResponseProductTaxDTO extends WrapperResponse<ProductTaxDTO> {}
export interface WrapperResponseInvoiceDTO extends WrapperResponse<InvoiceDTO> {}
export interface WrapperResponseSaleDTO extends WrapperResponse<SaleDTO> {}
export interface WrapperResponseReadFileResponse extends WrapperResponse<ReadFileResponse> {}
export interface WrapperResponseListReadFileResponse extends WrapperResponse<ReadFileResponse[]> {}
export interface WrapperResponseCFDIIssueResponse extends WrapperResponse<CFDIIssueResponse> {}
export interface WrapperResponseCustomerDTO extends WrapperResponse<CustomerDTO> {}
export interface WrapperResponseCustomerContactDTO extends WrapperResponse<CustomerContactDTO> {}
export interface WrapperResponseCustomerAddressDTO extends WrapperResponse<CustomerAddressDTO> {}
export interface WrapperResponseCompanyDTO extends WrapperResponse<CompanyDTO> {}
export interface WrapperResponseCompanyContactDTO extends WrapperResponse<CompanyContactDTO> {}
export interface WrapperResponseCompanyAddressDTO extends WrapperResponse<CompanyAddressDTO> {}
export interface WrapperResponseCompanySignImageDTO extends WrapperResponse<CompanySignImageDTO> {}
export interface WrapperResponsePostalCodeDTO extends WrapperResponse<PostalCodeDTO> {}
export interface WrapperResponsePlanDTO extends WrapperResponse<PlanDTO> {}
export interface WrapperResponseVoid extends WrapperResponse<null> {}

export interface WrapperResponseListUserDTO extends WrapperResponse<UserDTO[]> {}
export interface WrapperResponseListRoleDTO extends WrapperResponse<RoleDTO[]> {}
export interface WrapperResponseListPermissionDTO extends WrapperResponse<PermissionDTO[]> {}
export interface WrapperResponseListInvoiceDTO extends WrapperResponse<InvoiceDTO[]> {}
export interface WrapperResponseListSaleDTO extends WrapperResponse<SaleDTO[]> {}
export interface WrapperResponseListChargeDTO extends WrapperResponse<ChargeDTO[]> {}
export interface WrapperResponseChargeDTO extends WrapperResponse<ChargeDTO> {}
export interface WrapperResponseChargeSummaryDTO extends WrapperResponse<ChargeSummaryDTO> {}
export interface WrapperResponseBoardDTO extends WrapperResponse<BoardDTO> {}
export interface WrapperResponseListBoardDTO extends WrapperResponse<BoardDTO[]> {}
export interface WrapperResponseListPaymentAttemptDTO extends WrapperResponse<PaymentAttemptDTO[]> {}
export interface WrapperResponseListCompanyDTO extends WrapperResponse<CompanyDTO[]> {}
export interface WrapperResponseListPlanDTO extends WrapperResponse<PlanDTO[]> {}
export interface WrapperResponseListCountryDTO extends WrapperResponse<CountryDTO[]> {}

export interface WrapperPageableResponse<T = unknown> extends WrapperResponse<T[]> {
  page?: Page;
}

export interface WrapperPageableResponseProductDTO extends WrapperPageableResponse<ProductDTO> {}
export interface WrapperPageableResponseProductTaxDTO extends WrapperPageableResponse<ProductTaxDTO> {}
export interface WrapperPageableResponseTaxDTO extends WrapperPageableResponse<TaxDTO> {}
export interface WrapperPageableResponseTaxProductCodeDTO extends WrapperPageableResponse<TaxProductCodeDTO> {}
export interface WrapperPageableResponsePaymentLinkDTO extends WrapperPageableResponse<PaymentLinkDTO> {}
export interface WrapperPageableResponseListViewDTO extends WrapperPageableResponse<ListViewDTO> {}
export interface WrapperPageableResponseMeasureUnitDTO extends WrapperPageableResponse<MeasureUnitDTO> {}
export interface WrapperPageableResponseInvoiceDTO extends WrapperPageableResponse<InvoiceDTO> {}
export interface WrapperPageableResponseSaleDTO extends WrapperPageableResponse<SaleDTO> {}
export interface WrapperPageableResponseCustomerDTO extends WrapperPageableResponse<CustomerDTO> {}
export interface WrapperPageableResponseCustomerCardDTO extends WrapperPageableResponse<CustomerCardDTO> {}
export interface WrapperPageableResponseCurrencyDTO extends WrapperPageableResponse<CurrencyDTO> {}
export interface WrapperPageableResponseCountryDTO extends WrapperPageableResponse<CountryDTO> {}
export interface WrapperPageableResponseChargeDTO extends WrapperPageableResponse<ChargeDTO> {}
export interface WrapperPageableResponsePaymentDTO extends WrapperPageableResponse<PaymentRecordDTO> {}
export interface WrapperResponsePaymentDTO extends WrapperResponse<PaymentRecordDTO> {}
export interface WrapperPageableResponsePlanDTO extends WrapperPageableResponse<PlanDTO> {}

// =============================================================================
// FILTER SPECS (query params de búsqueda; vacíos en API = objeto libre)
// =============================================================================

export type UserFilterSpec = Record<string, unknown>;
export type ProductFilterSpec = Record<string, unknown>;
export type TaxFilterSpec = Record<string, unknown>;
export type ListTaxFilterSpec = Record<string, unknown>;
export type TaxProductCodeFilterSpec = Record<string, unknown>;
export type ListTaxProductCodeFilterSpec = Record<string, unknown>;
export type ListProductFilterSpec = Record<string, unknown>;
export type PaymentLinkFilterSpec = Record<string, unknown>;
export type MeasureUnitFilterSpec = Record<string, unknown>;
export type ListMeasureUnitFilterSpec = Record<string, unknown>;
export type SearchInvoiceFilterSpec = Record<string, unknown>;
export type ListInvoiceFilterSpec = Record<string, unknown>;
export type CustomerFilterSpec = Record<string, unknown>;
export type ListCustomerFilterSpec = Record<string, unknown>;
export type CurrencyFilterSpec = Record<string, unknown>;
export type ListCurrencyFilterSpec = Record<string, unknown>;
export type CountryFilterSpec = Record<string, unknown>;
export type CompanyFilterSpec = Record<string, unknown>;
export type SearchPlanFilterSpec = Record<string, unknown>;
export type ListPlanFilterSpec = Record<string, unknown>;
export type PaymentFilterSpec = Record<string, unknown>;
export type ListPaymentFilterSpec = Record<string, unknown>;

// =============================================================================
// DB-SCHEDULER / TASKS / LOGS / CONFIG
// =============================================================================

export interface TaskDetailsRequestParams {
  filter?: TaskFilterEnumValue;
  pageNumber?: number;
  size?: number;
  sorting?: TaskSortingEnumValue;
  asc?: boolean;
  searchTermTaskName?: string;
  searchTermTaskInstance?: string;
  taskNameExactMatch?: boolean;
  taskInstanceExactMatch?: boolean;
  startTime?: string;
  endTime?: string;
  refresh?: boolean;
  taskId?: string;
  taskName?: string;
}

export interface TaskRequestParams {
  filter?: TaskFilterEnumValue;
  pageNumber?: number;
  size?: number;
  sorting?: TaskSortingEnumValue;
  asc?: boolean;
  searchTermTaskName?: string;
  searchTermTaskInstance?: string;
  taskNameExactMatch?: boolean;
  taskInstanceExactMatch?: boolean;
  startTime?: string;
  endTime?: string;
  refresh?: boolean;
}

export interface TaskModel {
  taskName?: string;
  taskInstance?: string[];
  taskData?: unknown[];
  executionTime?: string[];
  picked?: boolean[];
  pickedBy?: string[];
  lastSuccess?: string[];
  lastFailure?: string;
  consecutiveFailures?: number[];
  lastHeartbeat?: string;
  version?: number;
}

export interface PollResponse {
  newFailures?: number;
  newRunning?: number;
  newTasks?: number;
  stoppedFailing?: number;
  finishedRunning?: number;
}

export interface GetTasksResponse {
  numberOfItems?: number;
  items?: TaskModel[];
  pageSize?: number;
  numberOfPages?: number;
}

export interface LogModel {
  id?: number;
  taskName?: string;
  taskInstance?: string;
  taskData?: Record<string, unknown>;
  timeStarted?: string;
  timeFinished?: string;
  succeeded?: boolean;
  durationMs?: number;
  exceptionClass?: string;
  exceptionMessage?: string;
  exceptionStackTrace?: string;
}

export interface LogPollResponse {
  newFailures?: number;
  newSucceeded?: number;
}

export interface GetLogsResponse {
  numberOfItems?: number;
  items?: LogModel[];
  pageSize?: number;
  numberOfPages?: number;
}

export interface ConfigResponse {
  showHistory?: boolean;
  readOnly?: boolean;
}

// =============================================================================
// WEBHOOKS
// =============================================================================

export interface CreateWebhookEventRequest {
  endpoint: string;
  description?: string;
  enabled?: boolean;
  headers?: Record<string, string>;
  subscribedEvents: WebhookEventType[];
}

export type WebhookEventType =
  | 'MIT_MANDATE_CREATED'
  | 'MIT_MANDATE_UPDATED'
  | 'MIT_MANDATE_CARD_EXPIRING'
  | 'MIT_TRANSACTION_CREATED'
  | 'MIT_TRANSACTION_UPDATED'
  | 'INVOICE_CREATED'
  | 'INVOICE_UPDATED'
  | 'CHARGE_OVERDUE'
  | 'CHARGE_DELINQUENT'
  | 'RECONCILIATION_CREATED'
  | 'PAYMENT_CREATED'
  | 'SUBSCRIPTION_CREATED'
  | 'SUBSCRIPTION_UPDATED'
  | 'SUBSCRIPTION_DELETED'
  | 'CUSTOMER_CREATED'
  | 'CUSTOMER_UPDATED'
  | 'CUSTOMER_DELETED'
  | 'PRODUCT_CREATED'
  | 'PRODUCT_UPDATED'
  | 'PRODUCT_DELETED'
  | 'SALE_CREATED'
  | 'SALE_UPDATED'
  | 'SALE_DELETED';

export interface WebhookDTO {
  id?: number;
  endpoint?: string;
  description?: string;
  enabled?: boolean;
  method?: string;
  headers?: Record<string, string>;
  secret?: string;
  subscribedEvents?: WebhookEventType[];
  createdAt?: string;
  updatedAt?: string;
}

export interface WrapperResponseWebhookDTO extends WrapperResponse<WebhookDTO> {}
export interface WrapperResponseListWebhookDTO extends WrapperResponse<WebhookDTO[]> {}

export interface WebhookEventDTO {
  id?: number;
  eventType?: string;
  payload?: string;
  status?: string;
  attemptsCount?: number;
  nextExecution?: string;
  lastAttemptAt?: string;
  webhookId?: number;
  webhookEndpoint?: string;
  webhookDescription?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface WebhookIntentDTO {
  id?: number;
  httpCode?: number;
  responseBody?: string;
  executionTimeMs?: number;
  success?: boolean;
  errorMessage?: string;
  createdAt?: string;
}

export interface WrapperResponseWebhookEventDTO extends WrapperResponse<WebhookEventDTO> {}
export interface WrapperPageableResponseWebhookEventDTO extends WrapperPageableResponse<WebhookEventDTO> {}
export interface WrapperPageableResponseWebhookIntentDTO extends WrapperPageableResponse<WebhookIntentDTO> {}

// =============================================================================
// BÚSQUEDA GLOBAL (GET /global-search)
// =============================================================================

/** Respuesta de `GlobalSearchIndexController` (lista directa, sin wrapper). */
export interface GlobalSearchIndexDTO {
  id?: number;
  entityType?: string | null;
  entityId?: string | null;
  companyId?: string | null;
  title?: string | null;
  description?: string | null;
  /** Ruta relativa de la app (p. ej. `/invoices/123`). */
  path?: string | null;
  /** Clase(s) de icono PrimeIcons, p. ej. `pi-file-invoice` o `pi pi-file`. */
  icon?: string | null;
  rawData?: Record<string, unknown> | null;
}

// =============================================================================
// PLANTILLAS DE COMUNICACIÓN
// =============================================================================

export type CommunicationType = 'CXC' | 'RENEWAL' | 'GENERAL';

export const CommunicationTypeOptions: { value: CommunicationType; label: string }[] = [
  { value: 'CXC', label: 'CXC' },
  { value: 'RENEWAL', label: 'Renovación' },
  { value: 'GENERAL', label: 'General' }
];

export function getCommunicationTypeLabel(type?: CommunicationType | null): string {
  return CommunicationTypeOptions.find((o) => o.value === type)?.label ?? type ?? '';
}

export interface CommunicationVarDTO {
  id?: number;
  varKey?: string;
  label?: string;
  description?: string;
  type?: CommunicationType;
  exampleValue?: string;
  sortOrder?: number;
}

export interface CommunicationTemplateDTO {
  id?: number;
  name?: string;
  subject?: string;
  messageBody?: string;
  messageTemplate?: string;
  subjectTemplate?: string;
  type?: CommunicationType;
  state?: EntityStateValue;
  createdAt?: string;
  updatedAt?: string;
}

export interface CommunicationTemplatePreviewDTO {
  subject?: string;
  body?: string;
}

export interface CreateCommunicationTemplateRequest {
  name: string;
  subject: string;
  messageBody: string;
  type: CommunicationType;
}

export interface PreviewCommunicationTemplateRequest {
  templateId: number;
  chargeId: number;
}

export interface WrapperResponseCommunicationTemplateDTO extends WrapperResponse<CommunicationTemplateDTO> {}
export interface WrapperResponseListCommunicationVarDTO extends WrapperResponse<CommunicationVarDTO[]> {}
export interface WrapperResponseCommunicationTemplatePreviewDTO extends WrapperResponse<CommunicationTemplatePreviewDTO> {}
export interface WrapperPageableResponseCommunicationTemplateDTO extends WrapperPageableResponse<CommunicationTemplateDTO> {}

// =============================================================================
// MIT (cobros bajo demanda con tarjeta guardada)
// =============================================================================

export const MitMandateStatus = {
  PENDING_ENROLLMENT: 'PENDING_ENROLLMENT',
  ACTIVE: 'ACTIVE',
  SUSPENDED: 'SUSPENDED',
  REVOKED: 'REVOKED',
  EXPIRED: 'EXPIRED'
} as const;
export type MitMandateStatusValue = (typeof MitMandateStatus)[keyof typeof MitMandateStatus];

export type MitExpiredReason = 'ENROLLMENT_LINK_EXPIRED' | 'ENROLLMENT_ATTEMPTS_EXCEEDED' | 'VALIDITY_ENDED';
export type MitEnrollmentPurpose = 'ENROLLMENT' | 'CARD_REPLACEMENT';
export type MitSuspendedBy = 'MERCHANT' | 'PENTO' | 'SYSTEM';
export type MitRevokedBy = 'MERCHANT' | 'CUSTOMER' | 'PENTO';
export type MitConsentType = 'ENROLLMENT' | 'LIMITS_CHANGE' | 'CARD_REPLACEMENT';

export const MitTransactionStatus = {
  PROCESSING: 'PROCESSING',
  APPROVED: 'APPROVED',
  DECLINED: 'DECLINED',
  REJECTED: 'REJECTED',
  PENDING_RECONCILIATION: 'PENDING_RECONCILIATION',
  /** Cargo de validación aprobado cuya cancelación no se ha confirmado. */
  VOID_PENDING: 'VOID_PENDING',
  VOIDED: 'VOIDED',
  /** Se agotó el plazo de 72 h para cancelar; requiere atención manual. */
  VOID_FAILED: 'VOID_FAILED'
} as const;
export type MitTransactionStatusValue = (typeof MitTransactionStatus)[keyof typeof MitTransactionStatus];

/** `CARD_VERIFICATION` se cancela y no genera venta; `INITIAL_CHARGE` es el primer cobro real al registrar la tarjeta. */
export type MitTransactionType = 'CARD_VERIFICATION' | 'INITIAL_CHARGE' | 'MIT_CHARGE';
export type MitRequestedByType = 'OWNER' | 'USER' | 'API';
export type MitResolutionSource = 'PROVIDER' | 'MANUAL';

export type MitRejectionCode =
  | 'MANDATE_NOT_ACTIVE'
  | 'MANDATE_NOT_YET_VALID'
  | 'MANDATE_OUT_OF_VALIDITY'
  | 'CARD_EXPIRED'
  | 'CARD_INACTIVE'
  | 'CARD_TYPE_NOT_ALLOWED'
  | 'PROVIDER_NOT_SUPPORTED'
  | 'CURRENCY_NOT_SUPPORTED'
  | 'AMOUNT_BELOW_MIN'
  | 'MAX_AMOUNT_PER_TRANSACTION'
  | 'PREVIOUS_TRANSACTION_UNRESOLVED'
  | 'MIN_INTERVAL'
  | 'POSSIBLE_DUPLICATE'
  | 'MAX_AMOUNT_PER_DAY'
  | 'MAX_TRANSACTIONS_PER_DAY'
  | 'MAX_AMOUNT_PER_MONTH'
  | 'MAX_TRANSACTIONS_PER_MONTH'
  | 'MAX_TOTAL_AMOUNT'
  | 'MAX_TOTAL_TRANSACTIONS'
  | 'COMPANY_DAILY_CAP'
  | 'COMPANY_RATE_LIMIT';

export interface MitLimits {
  minAmountPerTransaction?: number | null;
  maxAmountPerTransaction?: number | null;
  maxAmountPerDay?: number | null;
  maxAmountPerMonth?: number | null;
  maxTotalAmount?: number | null;
  maxTransactionsPerDay?: number | null;
  maxTransactionsPerMonth?: number | null;
  maxTotalTransactions?: number | null;
  minIntervalSeconds?: number | null;
  maxConsecutiveDeclines?: number | null;
}

export interface MitCardDTO {
  id?: number;
  lastDigits?: string;
  brand?: string;
  type?: string;
  expirationMonth?: string;
  expirationYear?: string;
}

export interface MitConsentDTO {
  id?: number;
  type?: MitConsentType;
  acceptedAt?: string;
  termsVersion?: string;
  termsTextSha256?: string;
  ipAddress?: string;
  cardLastDigits?: string;
}

/** Evidencia completa de un consentimiento: lo que el cliente leyó y aceptó, y desde dónde. */
export interface MitConsentDetailDTO {
  id: number;
  type?: MitConsentType;
  acceptedAt?: string;
  termsVersion?: string;
  termsText?: string;
  termsTextSha256?: string;
  limits?: MitLimits | null;
  validFrom?: string | null;
  validUntil?: string | null;
  cardLastDigits?: string | null;
  cardBrand?: string | null;
  cardExpirationMonth?: string | null;
  cardExpirationYear?: string | null;
  customerEmail?: string | null;
  signerName?: string | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  acceptLanguage?: string | null;
  deviceFingerprint?: string | null;
  deviceDetails?: Record<string, unknown> | null;
  transactionId?: number | null;
}

/** Datos del comercio que viajan a Paycode: texto o entero de 32 bits. */
export type MitComercialMetadata = Record<string, string | number>;

export interface MitMandateDTO {
  id: number;
  number?: string;
  status?: MitMandateStatusValue;
  expiredReason?: MitExpiredReason;
  customerId?: number;
  customerName?: string;
  customerEmail?: string;
  card?: MitCardDTO;
  paymentProvider?: string;
  currencyCode?: string;
  purpose?: string;
  externalReference?: string;
  /** Primer cobro real al registrar la tarjeta; sin él, el registro hace un cargo de validación que se cancela. */
  initialCharge?: MitInitialCharge | null;
  enrollmentExpiresAt?: string;
  enrollmentAttempts?: number;
  enrollmentPurpose?: MitEnrollmentPurpose;
  validFrom?: string;
  validUntil?: string;
  limits?: MitLimits;
  pendingLimits?: MitLimits;
  pendingLimitsExpiresAt?: string;
  consecutiveDeclines?: number;
  activatedAt?: string;
  suspendedAt?: string;
  suspendedBy?: MitSuspendedBy;
  suspendedReason?: string;
  revokedAt?: string;
  revokedBy?: MitRevokedBy;
  revokedReason?: string;
  lastChargedAt?: string;
  metadata?: Record<string, string>;
  comercialMetadata?: MitComercialMetadata;
  createdAt?: string;
  updatedAt?: string;
  /** Último envío de la liga de registro al correo del mandato; la liga nunca se expone al comercio. */
  enrollmentLinkSentAt?: string;
  consents?: MitConsentDTO[];
}

export interface MitTransactionDTO {
  id: number;
  type?: MitTransactionType;
  status?: MitTransactionStatusValue;
  mandateId?: number;
  mandateNumber?: string;
  customerId?: number;
  customerName?: string;
  amount?: number;
  currencyCode?: string;
  concept?: string;
  externalReference?: string;
  /** Solo en el detalle; vacío en cargos de validación. */
  lines?: MitChargeLine[] | null;
  metadata?: Record<string, string>;
  comercialMetadata?: MitComercialMetadata;
  rejectionCode?: MitRejectionCode;
  providerCode?: string;
  providerMessage?: string;
  providerReference?: string;
  cardLastDigits?: string;
  requestedByUserId?: number;
  requestedByName?: string;
  requestedByType?: MitRequestedByType;
  saleId?: number;
  saleNumber?: string;
  chargeId?: number;
  paymentId?: number;
  createdAt?: string;
  providerRespondedAt?: string;
  resolvedAt?: string;
  resolutionSource?: MitResolutionSource;
  resolutionNote?: string;
  materializationError?: string;
  /** Cancelación de un cargo de validación. */
  voidRequestedAt?: string;
  voidedAt?: string;
  voidDeadlineAt?: string;
  voidAttempts?: number;
  voidError?: string;
  limitsEvaluation?: Record<string, unknown>;
}

/** Línea de un cobro tal como se calculó al reservarlo; precio sin impuestos, total con impuestos. */
export interface MitChargeLine {
  productId?: number;
  description?: string;
  quantity?: number;
  unitPrice?: number;
  subtotal?: number;
  taxAmount?: number;
  totalAmount?: number;
  satObjetoImpId?: string;
}

export interface MitInitialChargeLine {
  productId?: number;
  description?: string;
  quantity?: number;
  /** Sin impuestos. */
  unitPrice?: number;
  taxAmount?: number;
  totalAmount?: number;
  taxes?: MitChargeLineTax[] | null;
}

export interface MitChargeLineTax {
  taxCode?: string;
  taxRate?: number;
  applicationType?: string;
  collectionType?: string;
}

export interface MitInitialCharge {
  concept?: string;
  /** Total con impuestos a centavos; es lo que se cobra. */
  amount?: number;
  lines?: MitInitialChargeLine[];
}

export interface MitChargeLineRequest {
  productId: number;
  quantity: number;
  /** Sin impuestos; si falta se usa el precio del producto sin impuestos. */
  unitPrice?: number;
  description?: string;
}

export interface MitUsageDTO {
  dayAmount?: number;
  dayCount?: number;
  monthAmount?: number;
  monthCount?: number;
  totalAmount?: number;
  totalCount?: number;
  effectiveLimits?: MitLimits;
}

export interface MitConfigDTO {
  enabled?: boolean;
  maxAmountPerTransactionCap?: number | null;
  maxAmountPerMonthPerMandateCap?: number | null;
  maxAmountPerDayCap?: number | null;
  maxTransactionsPerMinute?: number | null;
  maxMandateValidityDays?: number | null;
  /** Monto del cargo de validación al registrar o cambiar la tarjeta (se cancela de inmediato). */
  verificationAmount?: number | null;
  enrollmentLinkTtlHours?: number | null;
  allowedCardTypes?: string[] | null;
}

export interface UpdateMitConfigRequest extends MitConfigDTO {
  enabled: boolean;
}

export interface MitLimitsRequest extends MitLimits {
  maxAmountPerTransaction: number;
  maxAmountPerMonth: number;
}

export interface CreateMitMandateRequest {
  customerId: number;
  /** Concepto que el cliente ve y autoriza. */
  purpose: string;
  externalReference?: string;
  initialCharge?: { concept?: string; lines: MitChargeLineRequest[] };
  validFrom?: string;
  validUntil?: string;
  limits: MitLimitsRequest;
  metadata?: Record<string, string>;
  /** Se envía a Paycode en cada cargo del mandato. */
  comercialMetadata?: MitComercialMetadata;
}

export interface CreateMitTransactionRequest {
  lines: MitChargeLineRequest[];
  /** Opcional: si llega debe coincidir con la suma de las líneas. */
  amount?: number;
  currency?: string;
  concept: string;
  externalReference?: string;
  metadata?: Record<string, string>;
  /** Se fusiona con la del mandato (gana esta) y se envía a Paycode. */
  comercialMetadata?: MitComercialMetadata;
}

export interface MitMandateStatusChangeRequest {
  reason?: string;
}

export interface ResolveMitTransactionRequest {
  /** `VOIDED` confirma la cancelación de un cargo de validación hecha fuera de Pento. */
  outcome: 'APPROVED' | 'DECLINED' | 'VOIDED';
  providerReference?: string;
  /** Obligatorio al aprobar un cargo de validación. */
  providerTrackCode?: string;
  note?: string;
}

export interface PublicMitChargeDTO {
  type?: MitTransactionType;
  /** En un cargo de validación indica si ya se canceló (`VOIDED`). */
  status?: MitTransactionStatusValue;
  chargedAt?: string;
  concept?: string;
  amount?: number;
  /** Código de autorización del procesador. */
  reference?: string;
}

/** Tarjeta guardada del cliente con el comercio (sin token ni datos sensibles). */
export interface PublicMitSavedCardDTO {
  id: number;
  brand?: string;
  lastDigits?: string;
  expirationMonth?: string;
  expirationYear?: string;
  createdAt?: string;
  /** La usa este mandato. */
  currentForMandate?: boolean;
  /** La usa una suscripción o un mandato vigente; no se puede eliminar. */
  inUse?: boolean;
  /** Se puede elegir para el registro abierto en lugar de capturar otra tarjeta. */
  selectable?: boolean;
}

export interface PublicMitSignatureDTO {
  signerName?: string;
  signedAt?: string;
  ipAddress?: string;
  /** SHA-256 del texto de términos aceptado. */
  documentHash?: string;
  cardLastDigits?: string;
}

export interface PublicMitMandateDTO {
  id: number;
  number?: string;
  status?: MitMandateStatusValue;
  merchantName?: string;
  merchantLegalName?: string;
  merchantLogoUrl?: string;
  customerName?: string;
  customerLegalName?: string;
  purpose?: string;
  limits?: MitLimits;
  validFrom?: string;
  validUntil?: string;
  currencyCode?: string;
  issuedAt?: string;
  merchantTaxId?: string;
  merchantAddress?: string;
  enrollmentAvailable?: boolean;
  enrollmentPurpose?: MitEnrollmentPurpose;
  /** Cargo que se hará al registrar la tarjeta: validación que se cancela o primer cobro real. */
  enrollmentChargeType?: MitTransactionType;
  enrollmentChargeAmount?: number;
  enrollmentChargeConcept?: string;
  /** Solo para el primer cobro. */
  enrollmentChargeLines?: MitInitialChargeLine[];
  enrollmentExpiresAt?: string;
  currentCardLastDigits?: string;
  /** Tarjeta actual; solo en la vista de gestión. */
  currentCardBrand?: string;
  currentCardBank?: string;
  currentCardType?: string;
  currentCardExpirationMonth?: string;
  currentCardExpirationYear?: string;
  /** Precarga del registro; solo mientras el registro está disponible. */
  customerGivenNames?: string;
  customerPaternalName?: string;
  customerMaternalName?: string;
  customerEmail?: string;
  customerPhone?: string;
  pendingLimits?: MitLimits;
  pendingLimitsExpiresAt?: string;
  termsVersion?: string;
  termsText?: string;
  recentCharges?: PublicMitChargeDTO[];
  /** Comprobante: presente cuando el titular ya firmó un registro de tarjeta. */
  signature?: PublicMitSignatureDTO;
  /** Cargo del último registro de tarjeta firmado. */
  enrollmentCharge?: PublicMitChargeDTO;
  monthUsedAmount?: number;
  manageUrl?: string;
}

/** `customerMiddleName` es el apellido paterno y `customerLastName` el materno (formato Paycode). */
export interface PublicMitEnrollRequest {
  customerName: string;
  customerMiddleName: string;
  customerLastName?: string;
  customerEmail: string;
  customerPhone?: string;
  /** Tarjeta guardada con el comercio; excluye número, vigencia y CVV. */
  savedCardId?: number;
  cardNumber?: string;
  expMonth?: string;
  expYear?: string;
  cvv?: string;
  consentAccepted: boolean;
  termsVersion: string;
  /** Nombre completo escrito como firma electrónica. */
  signatureName: string;
  /** Datos técnicos del navegador como evidencia del consentimiento. */
  device?: Record<string, unknown>;
}

export interface PublicMitEnrollmentResultDTO {
  transactionStatus?: MitTransactionStatusValue;
  mandateStatus?: MitMandateStatusValue;
  message?: string;
  cardLastDigits?: string;
  remainingAttempts?: number;
  mandate?: PublicMitMandateDTO;
}

export interface PublicMitConsentRequest {
  consentAccepted: boolean;
  termsVersion: string;
  /** Datos técnicos del navegador como evidencia del consentimiento. */
  device?: Record<string, unknown>;
}

// ---------------------------------------------------------------- Notificaciones a clientes (headless)

export type CustomerNotificationMode = 'STANDARD' | 'HEADLESS_CUSTOM' | 'HEADLESS_FULL';

export type CustomerNotificationCategory = 'COLLECTION' | 'SUBSCRIPTION' | 'PAYMENT' | 'INVOICE' | 'COMMUNICATION' | 'MIT';

/** Tipo de correo al cliente final, del catálogo `GET /customer-notification-types`. */
export interface CustomerNotificationTypeDTO {
  name: string;
  category: CustomerNotificationCategory;
  label: string;
  description: string;
  /** Falso: se envía siempre (envío manual o aviso que protege al cliente). */
  configurable: boolean;
  lockedReason?: string | null;
  order: number;
  active: boolean;
}

export interface CustomerNotificationConfigDTO {
  mode: CustomerNotificationMode;
  /** Tipos automáticos permitidos; solo aplica en `HEADLESS_CUSTOM`. */
  enabled: string[];
}

export type UpdateCustomerNotificationConfigRequest = CustomerNotificationConfigDTO;
